/**
 * Procurement detail page — `/stock/procurement/:id`.
 *
 * Mobile-first: on viewports ≤ 768px (inclusive) the lines table folds
 * to a stack of tappable cards. The header section uses a Descriptions
 * grid that gracefully drops to a single column. We deliberately use a
 * hand-rolled `(max-width: 768px)` matchMedia query (via
 * `useMediaQuery`) rather than AntD's `Grid.useBreakpoint`: AntD's
 * `md` is true at width ≥ 768, so `!screens.md` is `< 768` and width
 * === 768 lands in the desktop branch — off-by-one vs the spec's
 * "≤ 768px = mobile" boundary.
 *
 * State machine (§3.6 — all 6 directed transitions present in the UI):
 *   draft     → [Mark received] [Cancel]
 *   received  → [Move back to draft] [Cancel]
 *   cancelled → [Reopen → received] [Move to draft] [Cancel — disabled]
 *
 * Every PATCH (header + state + line CRUD) carries an `If-Match:
 * <updated_at>` header. On 412 the page shows an inline banner so the
 * operator can reload deliberately rather than silently overwrite. On
 * 409 `stocktake_in_progress` we show a non-dismissable top banner with
 * TWO affordances per §6.3a: `[Go to stocktake]` and `[Wait for it to
 * finish]`. Picking "Wait" collapses the banner to a compact corner
 * pill while the 15s polling continues; the banner clears (and editing
 * re-enables) the moment the stocktake finalizes.
 *
 * Supplier picking is strict: typing filters existing suppliers via
 * /api/suppliers/suggest. If the operator submits a value that doesn't
 * exactly match a known supplier, a confirm modal asks:
 *   "No supplier 'X' found. Did you mean Y? [Use Y] [Create new] [Cancel]"
 * Even when no suggestion exists, the `[Use Y]` button is rendered
 * (disabled, with a "no match" hint) so the three-button layout stays
 * consistent per §6.5.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Divider,
  Input,
  Modal,
  Popconfirm,
  Radio,
  Row,
  Segmented,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { NumInput as InputNumber } from "../../components/NumInput";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";

import {
  ApiError,
  type ArticleRow,
  type ChainRow,
  type ExpenseCategoryRow,
  type ExpenseRow,
  type PfandValueRow,
  type ProcurementLineInput,
  type ProcurementLineRow,
  type ProcurementRow,
  type ProcurementState,
  type StocktakeInProgressPayload,
  type SupplierSuggestion,
  type VatRateRow,
  api,
} from "../../api/client";
import { StatePill } from "../../components/stock/StatePill";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { formatCents, formatCompactDateTime } from "../../utils/dateFormat";
import { grossToNet, netToGross } from "../../utils/vat";
import {
  DRAWER_FLOW_CODES,
  OPERATOR_HIDDEN_FOR_EXPENSES,
} from "../../utils/expenseCategoryCodes";
import { BackdateReceivedAtModal } from "./BackdateReceivedAtModal";

const { Title, Text } = Typography;

// ---------------------------------------------------------------------------
// Small utilities
// ---------------------------------------------------------------------------

function useDebounced<T>(value: T, ms = 200): T {
  const [out, setOut] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setOut(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return out;
}

/** Inspect an ApiError's body for a stocktake_in_progress 409 payload. */
function asStocktakeInProgress(
  err: unknown,
): StocktakeInProgressPayload | null {
  if (!(err instanceof ApiError)) return null;
  if (err.status !== 409) return null;
  // FastAPI wraps HTTPException details in `.detail` — the route raises
  // with `{detail: {error: "stocktake_in_progress", ...}}` so we have to
  // unwrap one level. The 412 path uses the same envelope.
  const body = err.body;
  if (body && typeof body === "object" && "detail" in body) {
    const d = (body as { detail: unknown }).detail;
    if (
      d &&
      typeof d === "object" &&
      (d as { error?: string }).error === "stocktake_in_progress"
    ) {
      return d as StocktakeInProgressPayload;
    }
  }
  return null;
}

function isIfMatchMismatch(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false;
  return err.status === 412;
}

// ---------------------------------------------------------------------------
// Supplier strict-pick (§6.5)
// ---------------------------------------------------------------------------

interface SupplierConfirmState {
  typed: string;
  suggestion: SupplierSuggestion | null;
}

interface SupplierPickerProps {
  value: string;
  /** Suggestions come from the parent (so it can debounce + cache). */
  suggestions: SupplierSuggestion[];
  onQueryChange: (next: string) => void;
  onCommit: (final: string) => void;
  disabled?: boolean;
}

/**
 * Strict-pick supplier field. Behaviour:
 *
 *   - As the user types, we just bubble the query up via `onQueryChange`
 *     so the parent can fetch suggestions. We DON'T commit on every
 *     keystroke (that would thrash PATCHes on the supplier field).
 *   - On blur OR Enter, if the typed value exactly matches a suggestion
 *     by `display_name`, commit immediately. Otherwise pop the confirm
 *     modal and only commit after the operator chooses Use-Y / Create-new.
 *
 * The committed value is the raw operator-typed string when "Create new"
 * is chosen, OR the suggestion's display_name when "Use Y" is chosen.
 * Either way the backend canonicalises it on write so the bucket key
 * stays sane.
 */
const SupplierPicker = ({
  value,
  suggestions,
  onQueryChange,
  onCommit,
  disabled,
}: SupplierPickerProps) => {
  const [local, setLocal] = useState(value);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingConfirm, setPendingConfirm] = useState<SupplierConfirmState | null>(
    null,
  );

  // Re-sync when parent value changes (e.g. server returns canonicalised
  // version after the user committed).
  useEffect(() => {
    setLocal(value);
  }, [value]);

  const tryCommit = () => {
    const typed = local.trim();
    if (typed === "" || typed === value.trim()) return;
    const exact = suggestions.find(
      (s) => s.display_name.toLowerCase() === typed.toLowerCase(),
    );
    if (exact) {
      // Snap to the canonical display name on exact match.
      onCommit(exact.display_name);
      return;
    }
    setPendingConfirm({ typed, suggestion: suggestions[0] ?? null });
    setConfirmOpen(true);
  };

  return (
    <>
      <Select
        showSearch
        value={local || undefined}
        onSearch={(v) => {
          setLocal(v);
          onQueryChange(v);
        }}
        onChange={(v) => {
          setLocal(v ?? "");
          if (v) onCommit(v);
        }}
        onBlur={tryCommit}
        // AntD's Select doesn't pass Enter through to the form when in
        // showSearch mode; capture it on the input ourselves.
        onInputKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            tryCommit();
          }
        }}
        placeholder="Type to search suppliers…"
        notFoundContent={null}
        allowClear
        disabled={disabled}
        style={{ minWidth: 240 }}
        options={suggestions.map((s) => ({
          value: s.display_name,
          label: s.display_name,
        }))}
        // We're using the typed value as filter source — server already
        // narrowed; show everything we got.
        filterOption={false}
      />
      <Modal
        open={confirmOpen}
        title="Unknown supplier"
        onCancel={() => setConfirmOpen(false)}
        // Buttons rendered manually in `footer` so we have three of them.
        footer={null}
      >
        {pendingConfirm && (
          <>
            <p>
              No supplier <Text strong>“{pendingConfirm.typed}”</Text> found.
              {pendingConfirm.suggestion && (
                <>
                  {" "}
                  Did you mean{" "}
                  <Text strong>“{pendingConfirm.suggestion.display_name}”</Text>?
                </>
              )}
            </p>
            <Space style={{ marginTop: 16 }} wrap>
              {/* §6.5 demands all three buttons even when no suggestion
                  exists — keep the `[Use Y]` slot visible (disabled, with
                  a "no match" hint) so the modal layout stays consistent
                  and the operator sees the affordance they'd get if the
                  fuzzy match had hit. */}
              {pendingConfirm.suggestion ? (
                <Button
                  type="primary"
                  onClick={() => {
                    onCommit(pendingConfirm.suggestion!.display_name);
                    setConfirmOpen(false);
                  }}
                >
                  Use {pendingConfirm.suggestion.display_name}
                </Button>
              ) : (
                <Tooltip title="No fuzzy match found in existing suppliers.">
                  <Button type="primary" disabled>
                    <Space size={6}>
                      <span>Use suggestion</span>
                      <Tag color="default" style={{ margin: 0 }}>
                        no match
                      </Tag>
                    </Space>
                  </Button>
                </Tooltip>
              )}
              <Button
                onClick={() => {
                  onCommit(pendingConfirm.typed);
                  setConfirmOpen(false);
                }}
              >
                Create new
              </Button>
              <Button
                onClick={() => {
                  // Revert local input — operator backed out.
                  setLocal(value);
                  setConfirmOpen(false);
                }}
              >
                Cancel
              </Button>
            </Space>
          </>
        )}
      </Modal>
    </>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

/**
 * Fix 6 — the "Add line" Form was replaced with a draft row rendered
 * as a Table.Summary.Row at the foot of the lines table. The draft
 * lives in client state until the operator hits "+ Add", at which
 * point we POST a new line and reset the draft.
 */
interface DraftLine {
  article_chain_root_id: number | null;
  qty: number | null;
  unit_cost_cents: number | null;
  crate_count: number | null;
}

const EMPTY_DRAFT: DraftLine = {
  article_chain_root_id: null,
  qty: null,
  unit_cost_cents: null,
  crate_count: null,
};

/**
 * Fix 7 — same idea on the expenses table. The "Add expense" Modal was
 * replaced with an inline draft row; the Pfand calculator is rendered
 * as a Card below the table when the chosen category is Pfand.
 */
interface DraftExpense {
  category_id: number | null;
  comment: string;
  /** Invoice-style ("cost-positive") cents, VAT-gross-up applied: a plain
   * positive value is a COST, a leading minus is a credit / reimbursement.
   * The whole procurement page reads this way — the article lines up top are
   * positive costs too. The sign is flipped to the DB's bank convention
   * (cost → negative `value_cents`) at submit; see onAddDraftExpense and
   * accounting.md §2. */
  value_cents: number | null;
  /** Independent Pfand cents (refactor 2026-06-04). Null means no
   * Pfand component on the row; non-null carries its own sign
   * independent of ``value_cents`` (per accounting.md §16). */
  pfand_value_cents: number | null;
  /** VAT rate id (FK into vat_rate); null = no rate (treated as 0% for
   * net math). The Pfand portion is always 19% regardless. */
  vat_rate_id: number | null;
}

// Note: the inline add-expense row no longer carries a `date` field —
// the procurement detail page POSTs without a date and the backend
// defaults the column to "today" (UTC). The Date column was likewise
// dropped from the expenses table; if the operator needs to back-date
// an expense, they do it from the expense workspace, not from here.
const makeEmptyDraftExpense = (): DraftExpense => ({
  category_id: null,
  comment: "",
  value_cents: null,
  pfand_value_cents: null,
  vat_rate_id: null,
});

/**
 * Auto-derived value tracking for fix 3.
 *
 * Keyed by line.id (or the literal "add-line" for the inline add form)
 * → set of field names the user has manually typed into. When a field
 * is NOT in the set, it was auto-derived (qty ↔ crate via pack_size, or
 * unit_cost prefilled from last-for-supplier), so the InputNumber is
 * rendered with `var(--ant-color-text-tertiary)` to hint "ghost / auto".
 *
 * Once the user explicitly edits the field, the field name is added to
 * the set and the tertiary override drops (the value is now "owned" by
 * the operator).
 */
type ManualEditMap = Map<string, Set<string>>;

const TERTIARY_STYLE: React.CSSProperties = {
  color: "var(--ant-color-text-tertiary)",
};

export const ProcurementDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const procurementId = Number(id);
  // §6.4: mobile = width ≤ 768px (inclusive). AntD's `Grid.useBreakpoint`
  // `md` is true at width ≥ 768, so it would push width === 768 into the
  // desktop branch — one pixel off. matchMedia `(max-width: 768px)` is
  // inclusive at the boundary, which matches the spec.
  const isMobile = useMediaQuery("(max-width: 768px)");

  const [row, setRow] = useState<ProcurementRow | null>(null);
  const [articles, setArticles] = useState<ArticleRow[]>([]);
  // Chain-grouped article list — single source of truth for article
  // name lookup by chain_root_id. Unlike `articles` (leaf view, active
  // only), chains include rows whose root is inactive after a price
  // change spawned a new leaf, so historical procurement lines still
  // resolve to a human name. See Fix 1 in the related change log.
  const [chains, setChains] = useState<ChainRow[]>([]);
  // VAT rates (incl. archived, so a line/expense pointing at an archived
  // rate still resolves) — powers the net columns + the VAT matrix.
  const [vatRates, setVatRates] = useState<VatRateRow[]>([]);
  // Expenses linked to this procurement (rendered in the "Expenses &
  // Pfand returns" section below the line items table).
  const [expenses, setExpenses] = useState<ExpenseRow[]>([]);
  const [expenseCategories, setExpenseCategories] = useState<ExpenseCategoryRow[]>([]);
  // Pfand value catalog — used by the Pfand calculator inside the
  // add-expense modal AND to resolve a chain's pfand_value_id /
  // crate_pfand_value_id FKs into cent values for the Article-mode
  // calculator.
  const [pfandValues, setPfandValues] = useState<PfandValueRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Serialise every mutation through a promise chain so two fast
  // edits (header → header, or line → line) don't race on the same
  // stale `row.updated_at` and trip 412. Without this each PATCH
  // captures the updated_at at the moment its onBlur fires; if two
  // PATCHes fire back-to-back, the second carries the pre-first
  // updated_at and the server rejects it as a stale snapshot.
  // The queue guarantees `runMutation` only runs while the previous
  // one has completed and its setRow has landed.
  const mutationChainRef = useRef<Promise<unknown>>(Promise.resolve());
  const runMutation = useCallback(<T,>(fn: () => Promise<T>): Promise<T> => {
    const next = mutationChainRef.current.then(fn, fn);
    mutationChainRef.current = next.catch(() => undefined);
    return next;
  }, []);

  // Concurrency banners.
  const [staleBanner, setStaleBanner] = useState(false);
  const [stocktakeBlock, setStocktakeBlock] =
    useState<StocktakeInProgressPayload | null>(null);
  // §6.3a: the 409 banner has two affordances — `[Go to stocktake]` and
  // `[Wait for it to finish]`. Picking "Wait" collapses the full alert
  // to a corner pill so the operator can keep scanning context while
  // the 15s polling continues; re-expansion happens automatically when
  // a fresh 409 fires (which shouldn't happen, since polling clears
  // the block on finalize — but the affordance is honest about that).
  const [stocktakeBlockCollapsed, setStocktakeBlockCollapsed] = useState(false);

  // Backdate-received_at modal (see
  // openspec/changes/add-procurement-backdate). Opened from the
  // "Received at" row's [Change] button when the procurement is in
  // ``received`` state.
  const [backdateOpen, setBackdateOpen] = useState(false);

  // Fix 2 / Fix 3 — per-line metadata for auto-fill behaviour.
  //
  // `lastBySupplierByArticle`: chain_root_id → unit_cost_cents from the
  // most-recent received order for the current supplier. Used as a
  // prefill source for unit-cost on the add-line form AND as a "ghost"
  // hint in existing draft lines whose unit_cost is still null.
  const [lastBySupplierByArticle, setLastBySupplierByArticle] = useState<
    Map<number, number>
  >(new Map());
  // `manualEdits`: per (line key, field name) flag for "the operator
  // explicitly typed this", used by Fix 3 to drop the tertiary colour
  // override once a field is owned by the operator. Stored in a ref +
  // mirror state so a render re-evaluates `isAuto` after a mutation.
  const [manualEdits, setManualEdits] = useState<ManualEditMap>(() => new Map());
  const markManual = useCallback((lineKey: string | number, field: string) => {
    setManualEdits((prev) => {
      const key = String(lineKey);
      const existing = prev.get(key);
      if (existing?.has(field)) return prev;
      const next = new Map(prev);
      const set = new Set(existing ?? []);
      set.add(field);
      next.set(key, set);
      return next;
    });
  }, []);
  const isAuto = useCallback(
    (lineKey: string | number, field: string): boolean =>
      !manualEdits.get(String(lineKey))?.has(field),
    [manualEdits],
  );
  // Fix 6 — inline-draft add-line state (replaces the old AntD Form).
  const [draftLine, setDraftLine] = useState<DraftLine>(EMPTY_DRAFT);

  // Fix 7 — inline-draft add-expense state (replaces the old Modal).
  const [draftExpense, setDraftExpense] = useState<DraftExpense>(
    makeEmptyDraftExpense,
  );

  // Net/brutto entry mode (Part 2). Storage stays GROSS always; in net mode
  // the editable goods fields (Lines unit cost + Expenses value) accept a
  // NET figure and we convert to gross on save via net_to_gross. Pfand stays
  // gross entry. Toggling re-derives the displayed numbers from the stored
  // gross, so no values are lost — only the rounding may shift by ≤1 cent.
  const [netEntry, setNetEntry] = useState(false);

  // Focused-edit buffer. A numeric InputNumber whose value is derived from
  // server state gets RESET to that server value whenever an async commit /
  // refetch lands mid-typing — that's the "123456 → 1236" revert (the
  // debounced commit or a sibling field's refetch overwrites local state
  // while the operator is still typing). While a field is focused we hold the
  // operator's raw typed number here and render IT, so no background update
  // can clobber the input. Cleared on blur (after an optimistic local write
  // so the field doesn't flash the pre-commit value). Single slot — only one
  // input is focused at a time. Keyed by the same id used for debouncing.
  const [activeEdit, setActiveEdit] = useState<{
    key: string;
    raw: number | null;
  } | null>(null);
  const editedValue = (
    key: string,
    fallback: number | undefined,
  ): number | undefined =>
    activeEdit && activeEdit.key === key
      ? activeEdit.raw ?? undefined
      : fallback;
  // Optimistically splice a value into a single procurement line so clearing
  // the focus buffer on blur doesn't flash the pre-commit number.
  const optimisticLine = (lineId: number, patch: Partial<ProcurementLineRow>) =>
    setRow((prev) =>
      prev
        ? {
            ...prev,
            lines: prev.lines.map((l) =>
              l.id === lineId ? { ...l, ...patch } : l,
            ),
          }
        : prev,
    );
  const optimisticExpense = (
    expId: number,
    patch: Partial<ExpenseRow>,
  ) =>
    setExpenses((prev) =>
      prev.map((e) => (e.id === expId ? { ...e, ...patch } : e)),
    );

  // Fix 2 — debounce arrow-control onChange events so rapid ▲/▼ clicks
  // don't fire one PATCH per tick. Keyed by `${line.id}-${field}` so each
  // input has its own timer. Typed values still commit via onBlur as a
  // fallback when the operator clicks away before the debounce flushes.
  const debouncedCommitRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map(),
  );
  const debouncedCommit = useCallback((key: string, fn: () => void) => {
    const m = debouncedCommitRef.current;
    const existing = m.get(key);
    if (existing) clearTimeout(existing);
    const t = setTimeout(() => {
      m.delete(key);
      fn();
    }, 250);
    m.set(key, t);
  }, []);
  // Clean up any pending timers when the page unmounts so we don't fire
  // a PATCH on a torn-down component.
  useEffect(() => {
    const map = debouncedCommitRef.current;
    return () => {
      for (const t of map.values()) clearTimeout(t);
      map.clear();
    };
  }, []);

  // Supplier autocomplete state (shared across header + add-line use).
  const [supplierQuery, setSupplierQuery] = useState("");
  const [supplierSuggestions, setSupplierSuggestions] = useState<SupplierSuggestion[]>(
    [],
  );
  const debouncedSupplierQuery = useDebounced(supplierQuery);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.suggestSuppliers(debouncedSupplierQuery || undefined);
        if (!cancelled) setSupplierSuggestions(res);
      } catch {
        if (!cancelled) setSupplierSuggestions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [debouncedSupplierQuery]);

  const refresh = useCallback(async () => {
    if (!Number.isFinite(procurementId)) return;
    setLoading(true);
    setError(null);
    try {
      // The chains call is the authoritative name source by chain_root_id
      // — `listArticles({active:true})` returns only the active leaf, so
      // a procurement line pointing at an inactive chain root would miss
      // and the article column would fall back to the raw id (bug fix).
      // We keep `listArticles` around for the sell-price label in the
      // picker dropdown (chains carry the leaf sell price but not the
      // historical buy price, which the dropdown shows for context).
      const [proc, arts, chainRows, exps, expCats, pfands] = await Promise.all([
        api.getProcurement(procurementId),
        api.listArticles({ active: true }),
        api.listArticleChains(),
        api.listProcurementExpenses(procurementId),
        api.listExpenseCategories({ include_archived: false }),
        api.listPfandValues(false),
      ]);
      setRow(proc);
      setArticles(arts);
      setChains(chainRows);
      setExpenses(exps);
      setExpenseCategories(expCats);
      setPfandValues(pfands);
      // VAT rates are loaded separately (non-blocking-ish, tiny list) so a
      // VAT endpoint hiccup never blanks the whole procurement page.
      void api
        .listVatRates(true)
        .then(setVatRates)
        .catch(() => setVatRates([]));
      setStaleBanner(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [procurementId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Fix 2: build a chain_root_id → unit_cost_cents map from the
  // last-received order for the current supplier. Used as a prefill
  // source on the add-line form (and a "ghost" hint on existing draft
  // lines whose unit_cost is still null). Reset whenever the supplier
  // changes — including to empty — so stale prefills don't leak.
  useEffect(() => {
    const supplier = row?.supplier ?? "";
    if (!supplier) {
      setLastBySupplierByArticle(new Map());
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const last = await api.lastForSupplier(supplier);
        if (cancelled) return;
        const m = new Map<number, number>();
        if (last) {
          for (const l of last.lines) {
            if (l.unit_cost_cents !== null && !m.has(l.article_chain_root_id)) {
              m.set(l.article_chain_root_id, l.unit_cost_cents);
            }
          }
        }
        setLastBySupplierByArticle(m);
      } catch {
        if (!cancelled) setLastBySupplierByArticle(new Map());
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [row?.supplier]);

  // Fix 1 (auto-PATCH prefill) — when the supplier is known and the
  // last-for-supplier map is populated, walk every line whose unit_cost
  // is still null and PATCH the suggestion in. We deliberately do NOT
  // markManual: the value stays "auto" so the tertiary-color hint kicks
  // in, telling the operator "this was auto-filled, edit to override".
  useEffect(() => {
    if (!row || row.lines.length === 0) return;
    if (lastBySupplierByArticle.size === 0) return;
    void (async () => {
      for (const line of row.lines) {
        if (line.unit_cost_cents !== null) continue;
        const suggested = lastBySupplierByArticle.get(line.article_chain_root_id);
        if (suggested === undefined) continue;
        try {
          // Don't markManual — leave as "auto" so the tertiary style
          // applies in the column render.
          await patchLine(line, { unit_cost_cents: suggested });
        } catch {
          // Swallow — operator can still type a value manually.
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastBySupplierByArticle, row?.lines.length, row?.supplier]);

  // Poll the in-progress stocktake every 15s while blocked. Clear the
  // banner the moment nothing in-progress comes back.
  useEffect(() => {
    if (!stocktakeBlock) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const open = await api.listStocktakes({ finalized: false, limit: 1 });
        if (!cancelled && open.length === 0) {
          setStocktakeBlock(null);
          // Reset collapsed so the NEXT block (if any) starts expanded.
          setStocktakeBlockCollapsed(false);
          void refresh();
        }
      } catch {
        // Ignore transient errors — we'll try again on the next tick.
      }
    };
    // Leading tick: a stocktake that finalizes seconds after the block
    // appeared shouldn't make the operator wait the full 15s window.
    void tick();
    const t = setInterval(tick, 15_000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [stocktakeBlock, refresh]);

  // Centralised error handler for every PATCH/POST/DELETE on this page.
  // Translates 412 → stale banner and 409 stocktake_in_progress → block
  // banner. Returns `true` if the error was handled (caller should not
  // re-throw / display a generic alert).
  const handleEditError = useCallback((err: unknown): boolean => {
    const blocking = asStocktakeInProgress(err);
    if (blocking) {
      setStocktakeBlock(blocking);
      // A NEW 409 re-expands the banner even if the operator had
      // previously collapsed it — they need to see "still blocked".
      setStocktakeBlockCollapsed(false);
      return true;
    }
    if (isIfMatchMismatch(err)) {
      setStaleBanner(true);
      return true;
    }
    return false;
  }, []);

  // ---------------------------------------------------------------------
  // Article-name + picker resolution (Fix 1)
  //
  // The Article column on a procurement line is keyed by chain_root_id.
  // The original implementation built the lookup from
  // `listArticles({active:true})` — the leaf view, active-only — so a
  // line pointing at a chain root whose root is inactive (typical after
  // a price change spawned a new leaf) couldn't resolve and the column
  // collapsed to the raw "16". `listArticleChains()` is the authoritative
  // source: it returns one row per chain_root_id, with the active-leaf
  // name as the headline, regardless of root-active state.
  //
  // The picker dropdown ALSO needs a sell-price label per article; we
  // keep `listArticles` around for that and stitch the two sources by
  // chain_root_id. Fallback order: chain.name → article.name → `Article #N`
  // (so we never render the raw id alone).
  // ---------------------------------------------------------------------
  const articleNameById = useMemo(() => {
    const m = new Map<number, string>();
    for (const c of chains) m.set(c.chain_root_id, c.name);
    // Backfill from leaf articles for chain roots not yet in chains (this
    // shouldn't happen in practice, but keeps the map total over both
    // sources).
    for (const a of articles) {
      if (!m.has(a.chain_root_id)) m.set(a.chain_root_id, a.name);
    }
    return m;
  }, [chains, articles]);

  const articleOptions = useMemo(() => {
    // Build options from chains (covers inactive-root cases). The label
    // mirrors the previous "Name (price)" shape; price falls back to the
    // leaf-article entry if a chain row lacks a sell price.
    const opts = chains.map((c) => ({
      value: c.chain_root_id,
      label: `${c.name} (${formatCents(c.sell_price_cents)})`,
    }));
    // Backfill from leaf articles in case any chain_root_id is missing.
    const seen = new Set(opts.map((o) => o.value));
    for (const a of articles) {
      if (!seen.has(a.chain_root_id)) {
        opts.push({
          value: a.chain_root_id,
          label: `${a.name} (${formatCents(a.sell_price_cents)})`,
        });
        seen.add(a.chain_root_id);
      }
    }
    return opts;
  }, [chains, articles]);


  const editingDisabled = Boolean(stocktakeBlock) || row === null;
  const lineEditDisabled = editingDisabled;

  // -------------------------------------------------------------------------
  // Header edits
  // -------------------------------------------------------------------------

  const patchHeader = (
    payload: Parameters<typeof api.patchProcurementHeader>[1],
  ): Promise<void> =>
    runMutation(async () => {
      // Read the latest known parent inside the queued task so each
      // header PATCH carries the post-previous-PATCH updated_at, not
      // the stale closure value from when the input first blurred.
      const current = await api.getProcurement(procurementId);
      try {
        const next = await api.patchProcurementHeader(
          current.id,
          payload,
          current.updated_at,
        );
        setRow(next);
      } catch (err) {
        if (handleEditError(err)) return;
        setError(err instanceof ApiError ? err.message : String(err));
      }
    });

  const onSupplierCommit = (next: string) => {
    if (!row) return;
    if ((row.supplier ?? "") === next) return;
    void patchHeader({ supplier: next || null });
  };

  // -------------------------------------------------------------------------
  // State transitions
  // -------------------------------------------------------------------------

  const transition = (
    next: ProcurementState,
    options: { paid_in_cash?: boolean } = {},
  ): Promise<void> =>
    runMutation(async () => {
      const current = await api.getProcurement(procurementId);
      try {
        const updated = await api.patchProcurementState(
          current.id,
          next,
          current.updated_at,
          options,
        );
        setRow(updated);
      } catch (err) {
        if (handleEditError(err)) return;
        setError(err instanceof ApiError ? err.message : String(err));
      }
    });

  // -------------------------------------------------------------------------
  // Line CRUD (add via inline form below the table; edit/delete in-place)
  // -------------------------------------------------------------------------

  // Refetch the parent + push it through setRow. Used after every line
  // mutation since the line endpoints return the LINE (not the parent
  // ProcurementRow), and the cascade of subsequent mutations needs the
  // post-write updated_at to avoid 412 races. Returns the latest row so
  // queued callers can read it synchronously.
  const refetchAndSetRow = useCallback(async (): Promise<ProcurementRow | null> => {
    if (!Number.isFinite(procurementId)) return null;
    try {
      const fresh = await api.getProcurement(procurementId);
      setRow(fresh);
      return fresh;
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      return null;
    }
  }, [procurementId]);

  // Fix 6 — submit the inline draft line as a new procurement line and
  // clear the draft. The draft replaces the AntD Form-based add row;
  // qty / crates / unit_cost auto-derive on the draft client-side, so
  // we just hand the final shape to the backend.
  const onAddDraftLine = (): Promise<void> =>
    runMutation(async () => {
      const articleId = draftLine.article_chain_root_id;
      const qty = draftLine.qty;
      if (articleId === null || qty === null || qty <= 0) return;
      const payload: ProcurementLineInput = {
        article_chain_root_id: articleId,
        qty,
        unit_cost_cents: draftLine.unit_cost_cents,
        crate_count: draftLine.crate_count,
      };
      const current = await api.getProcurement(procurementId);
      try {
        await api.addProcurementLine(current.id, payload, current.updated_at);
        setDraftLine(EMPTY_DRAFT);
        await refetchAndSetRow();
      } catch (err) {
        if (handleEditError(err)) return;
        if (err instanceof ApiError) setError(err.message);
      }
    });

  const patchLine = (
    line: ProcurementLineRow,
    next: Partial<ProcurementLineInput>,
  ): Promise<void> =>
    runMutation(async () => {
      const payload: ProcurementLineInput = {
        article_chain_root_id: next.article_chain_root_id ?? line.article_chain_root_id,
        qty: next.qty ?? line.qty,
        unit_cost_cents:
          "unit_cost_cents" in next ? next.unit_cost_cents ?? null : line.unit_cost_cents,
        crate_count:
          "crate_count" in next ? next.crate_count ?? null : line.crate_count,
      };
      const current = await api.getProcurement(procurementId);
      try {
        await api.patchProcurementLine(current.id, line.id, payload, current.updated_at);
        await refetchAndSetRow();
      } catch (err) {
        if (handleEditError(err)) return;
        if (err instanceof ApiError) setError(err.message);
      }
    });

  const deleteLine = (line: ProcurementLineRow): Promise<void> =>
    runMutation(async () => {
      const current = await api.getProcurement(procurementId);
      try {
        await api.deleteProcurementLine(current.id, line.id, current.updated_at);
        await refetchAndSetRow();
      } catch (err) {
        if (handleEditError(err)) return;
        if (err instanceof ApiError) setError(err.message);
      }
    });

  // Hard-delete the draft procurement and navigate back to the list. The
  // backend refuses anything but ``draft``; we hide the button outside the
  // draft state so the operator never sees a 400.
  const onDeleteDraft = (): Promise<void> =>
    runMutation(async () => {
      try {
        const current = await api.getProcurement(procurementId);
        await api.deleteProcurement(current.id, current.updated_at);
        navigate("/stock/procurement");
      } catch (err) {
        if (handleEditError(err)) return;
        if (err instanceof ApiError) setError(err.message);
      }
    });

  // -------------------------------------------------------------------------
  // Copy lines from last order — only available in draft + supplier set
  // -------------------------------------------------------------------------

  const [copyBusy, setCopyBusy] = useState(false);
  const copyFromLastOrder = (): Promise<void> =>
    runMutation(async () => {
      if (!row || !row.supplier) return;
      setCopyBusy(true);
      try {
        const last = await api.lastForSupplier(row.supplier);
        if (!last) {
          setError(`No previous received order for supplier "${row.supplier}".`);
          return;
        }
        for (const line of last.lines) {
          try {
            const current = await api.getProcurement(procurementId);
            await api.addProcurementLine(
              current.id,
              {
                article_chain_root_id: line.article_chain_root_id,
                qty: line.qty,
                unit_cost_cents: line.unit_cost_cents,
                crate_count: line.crate_count,
              },
              current.updated_at,
            );
          } catch (err) {
            if (handleEditError(err)) return;
            throw err;
          }
        }
        await refetchAndSetRow();
      } catch (err) {
        if (err instanceof ApiError) setError(err.message);
      } finally {
        setCopyBusy(false);
      }
    });

  // -------------------------------------------------------------------------
  // Expenses & Pfand returns (Fix 2 / Fix 3)
  //
  // The section lives below the line items and lists every Expense
  // attached to this procurement via `linked_procurement_id`. The
  // operator adds rows via an inline draft row, invoice-style: a plain
  // positive value is a cost, a leading minus is a credit / reimbursement;
  // the sign is flipped to the DB bank convention at submit (accounting.md
  // §2). An optional Pfand calculator pre-fills the magnitude + comment when
  // the chosen category is "Pfand" (Article or Pfand-type sub-modes). The
  // calculator's output
  // is non-destructive — it just populates the fields, and the operator
  // can still edit before saving. Solo bottles vs solo crates are
  // handled by submitting two separate expense rows (one per type).
  // -------------------------------------------------------------------------

  // Cache the resolved "Pfand" category id so we can toggle the
  // calculator UI without string-matching every render. Looked up by
  // immutable ``code`` (post-migration-0024) so renaming the category
  // display name doesn't break the calculator.
  const pfandCategoryId = useMemo(() => {
    const m = expenseCategories.find((c) => c.code === "pfand");
    return m?.id ?? null;
  }, [expenseCategories]);

  const expenseCategoryById = useMemo(() => {
    const m = new Map<number, ExpenseCategoryRow>();
    for (const c of expenseCategories) m.set(c.id, c);
    return m;
  }, [expenseCategories]);

  // Whether the procurement was paid in cash — derived from the linked
  // expenses (a drawer-flow expense exists on the refill iff
  // ``paid_in_cash=true`` was used at receive-time or settled later via
  // the standalone "pay from drawer" endpoint). Drives the drawer-event
  // note in the backdate modal (D7).
  const paidInCash = useMemo(() => {
    return expenses.some((e) => {
      const cat = expenseCategoryById.get(e.category_id);
      return (
        cat !== undefined &&
        (DRAWER_FLOW_CODES as readonly string[]).includes(cat.code)
      );
    });
  }, [expenses, expenseCategoryById]);

  // Chain metadata lookup by chain_root_id — used by the Article-mode
  // calculator (pack_size + pfand FKs → cents via pfandValues catalog).
  const chainByRoot = useMemo(() => {
    const m = new Map<number, ChainRow>();
    for (const c of chains) m.set(c.chain_root_id, c);
    return m;
  }, [chains]);

  const pfandValueById = useMemo(() => {
    const m = new Map<number, PfandValueRow>();
    for (const p of pfandValues) m.set(p.id, p);
    return m;
  }, [pfandValues]);

  // Fix 7 — calculator-mode state for the inline Pfand calculator.
  // (The Modal+Form that used to host these was killed; the draft
  // expense lives in `draftExpense` and the calculator panel is a Card
  // rendered below the table when category === Pfand.)
  const [calcMode, setCalcMode] = useState<"article" | "pfand_type">("article");
  const [calcArticleId, setCalcArticleId] = useState<number | null>(null);
  const [calcCratesReturned, setCalcCratesReturned] = useState<number>(1);
  const [calcPfandValueId, setCalcPfandValueId] = useState<number | null>(null);
  const [calcQty, setCalcQty] = useState<number>(1);
  const [calcSectionOpen, setCalcSectionOpen] = useState(false);

  const refreshExpenses = useCallback(async () => {
    if (!Number.isFinite(procurementId)) return;
    try {
      const rows = await api.listProcurementExpenses(procurementId);
      setExpenses(rows);
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
    }
  }, [procurementId]);

  // PATCH a single expense field (value_cents / pfand_value_cents) and
  // splice the updated row into local state. Serialised through the same
  // mutation chain as procurement-line edits so a fast pair of edits on
  // different fields of the same row can't race on stale ``updated_at``.
  // On error we refresh the whole list to reset to server-truth.
  const patchExpenseField = useCallback(
    (
      exp: ExpenseRow,
      payload: {
        value_cents?: number;
        pfand_value_cents?: number | null;
        category_id?: number;
        comment?: string | null;
        vat_rate_id?: number | null;
      },
    ) => {
      void runMutation(async () => {
        try {
          // No If-Match: the ``exp.updated_at`` captured in the input's
          // onChange closure goes stale after the first keystroke commits,
          // so passing it 412s on the next keystroke and the error-path
          // refreshExpenses() clobbers the field mid-typing (e.g. 123456 →
          // 1236). The line-items table avoids this by fetching a fresh
          // updated_at per edit; here runMutation already serialises edits
          // on this single-operator page, so per-keystroke optimistic
          // concurrency isn't needed.
          const updated = await api.patchExpense(exp.id, payload);
          setExpenses((prev) =>
            prev.map((e) => (e.id === exp.id ? updated : e)),
          );
        } catch (err) {
          if (err instanceof ApiError) setError(err.message);
          await refreshExpenses();
        }
      });
    },
    [runMutation, refreshExpenses],
  );

  // Submit the inline draft expense. The operator enters the value the way
  // it reads on the invoice ("cost-positive"): a plain positive number is a
  // cost, a leading minus is a credit / reimbursement. We flip that to the
  // DB's bank convention at submit (cost → negative value_cents), so the
  // forgotten-minus footgun is gone — a bare positive is always a cost.
  // Pfand follows the SAME invoice-style convention (accounting.md §16): a
  // positive Pfand is a deposit you PAID (cost → stored negative, so it reads
  // negative in the Expenses view exactly like a delivery's line-item Pfand);
  // a leading minus is a Pfand refund / return (stored positive).
  const onAddDraftExpense = async () => {
    // AntD InputNumber can transiently emit NaN / Infinity during fast
    // typing, and category_id likewise needs an integer-shape check.
    // Math.round(NaN) === NaN slips past ``!== 0`` checks but gets
    // rejected server-side as "not a valid integer" — belt-and-braces
    // guard them here.
    if (
      draftExpense.category_id === null ||
      !Number.isFinite(draftExpense.category_id) ||
      draftExpense.category_id <= 0
    ) {
      setError("Choose a category for the expense.");
      return;
    }
    // At least one of Value / Pfand must be non-zero — a bare "I want
    // to attach a comment to nothing" row is rejected server-side by
    // the CHECK constraint; a clean 400 from us beats letting the
    // request hit the wire.
    const valueIsValid =
      draftExpense.value_cents !== null &&
      Number.isFinite(draftExpense.value_cents) &&
      draftExpense.value_cents !== 0;
    const pfandIsValid =
      draftExpense.pfand_value_cents !== null &&
      Number.isFinite(draftExpense.pfand_value_cents) &&
      draftExpense.pfand_value_cents !== 0;
    if (!valueIsValid && !pfandIsValid) {
      setError("Enter a non-zero Value or Pfand for the expense.");
      return;
    }
    // Invoice-style → bank convention: negate BOTH value and Pfand. A positive
    // invoice value / Pfand is a cost (stored negative); a typed minus is a
    // credit / refund (stored positive).
    const value_cents = valueIsValid
      ? -Math.round(draftExpense.value_cents as number)
      : 0;
    const pfand_value_cents = pfandIsValid
      ? -Math.round(draftExpense.pfand_value_cents as number)
      : null;
    if (!Number.isFinite(value_cents)) {
      setError("Invalid Value for the expense.");
      return;
    }
    try {
      await api.createExpense({
        category_id: draftExpense.category_id,
        value_cents,
        pfand_value_cents,
        vat_rate_id: draftExpense.vat_rate_id,
        comment: draftExpense.comment.trim() ? draftExpense.comment : null,
        linked_procurement_id: procurementId,
      });
      setDraftExpense(makeEmptyDraftExpense());
      // Reset calculator state too so the next round starts clean.
      setCalcMode("article");
      setCalcArticleId(null);
      setCalcCratesReturned(1);
      setCalcPfandValueId(null);
      setCalcQty(1);
      setCalcSectionOpen(false);
      await refreshExpenses();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  };

  const onDeleteExpense = async (exp: ExpenseRow) => {
    try {
      await api.deleteExpense(exp.id);
      await refreshExpenses();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  };

  // ---- Calculator: compute the previewed cents for the active sub-mode.
  // Returns null when the inputs aren't sufficient yet (e.g. no article
  // picked, or the article has no Pfand metadata). The "Use this value"
  // button is disabled in that state.
  const calculatorPreview = useMemo<
    { cents: number; comment: string } | null
  >(() => {
    if (calcMode === "article") {
      if (calcArticleId === null) return null;
      const chain = chainByRoot.get(calcArticleId);
      if (!chain) return null;
      const packSize = chain.pack_size ?? 0;
      const bottlePfand =
        chain.pfand_value_id !== null
          ? pfandValueById.get(chain.pfand_value_id)?.cents ?? 0
          : 0;
      const cratePfand =
        chain.crate_pfand_value_id !== null
          ? pfandValueById.get(chain.crate_pfand_value_id)?.cents ?? 0
          : 0;
      if (bottlePfand === 0 && cratePfand === 0) return null;
      const perCrate = packSize * bottlePfand + cratePfand;
      const n = Math.max(0, Math.round(calcCratesReturned || 0));
      const cents = n * perCrate;
      const eur = (v: number) =>
        new Intl.NumberFormat("de-DE", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(v / 100);
      const comment =
        `Returned ${n} full crates of ${chain.name}` +
        ` (${packSize} bottles × €${eur(bottlePfand)}` +
        ` + €${eur(cratePfand)} crate)`;
      return { cents, comment };
    }
    // Pfand-type mode.
    if (calcPfandValueId === null) return null;
    const pv = pfandValueById.get(calcPfandValueId);
    if (!pv) return null;
    const qty = Math.max(0, Math.round(calcQty || 0));
    const cents = qty * pv.cents;
    const eur = (v: number) =>
      new Intl.NumberFormat("de-DE", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(v / 100);
    const comment = `Returned ${qty} × ${pv.label} (€${eur(pv.cents)} each)`;
    return { cents, comment };
  }, [
    calcMode,
    calcArticleId,
    calcCratesReturned,
    calcPfandValueId,
    calcQty,
    chainByRoot,
    pfandValueById,
  ]);

  // Fill the draft-expense magnitude + comment from the calculator preview.
  // The calculator computes a Pfand RETURN to the supplier (crates handed
  // back — cash in, a refund). Under the invoice-style Pfand convention a
  // refund is entered as a NEGATIVE (positive = a deposit you PAID), so prefill
  // the negated magnitude; submit negates again, storing the refund as a
  // positive (inflow) pfand_value_cents. The operator flips to positive for the
  // rarer case of PAYING Pfand to the supplier.
  const applyCalculator = () => {
    if (!calculatorPreview) return;
    setDraftExpense((d) => ({
      ...d,
      pfand_value_cents: -calculatorPreview.cents,
      comment: calculatorPreview.comment,
    }));
    // Optionally collapse the calculator after applying — the spec
    // marked this optional; collapsing is the less intrusive choice.
    setCalcSectionOpen(false);
  };

  // Pfand-aware article options for the calculator: chains that have at
  // least one pfand FK set. We surface inactive-root chains here too —
  // the operator might be returning crates of a discontinued article.
  const pfandArticleOptions = useMemo(
    () =>
      chains
        .filter(
          (c) => c.pfand_value_id !== null || c.crate_pfand_value_id !== null,
        )
        .map((c) => ({
          value: c.chain_root_id,
          label: `${c.name} (pack ${c.pack_size ?? "?"})`,
        })),
    [chains],
  );

  // -------------------------------------------------------------------------
  // VAT / net helpers (buchhalterisch). Prices are stored GROSS; the net
  // columns below are display-only (read-only — the net/brutto entry switch
  // is a later step). Pfand is always 19% (id 1). These power the per-row
  // "Net value" / "Net Pfand 19%" columns in both tables.
  // -------------------------------------------------------------------------
  const vatPercentById = useMemo(() => {
    const m = new Map<number, number>();
    for (const v of vatRates) m.set(v.id, v.percent);
    return m;
  }, [vatRates]);
  // Resolve a rate id → percent, mirroring the matrix fallbacks: a missing
  // rate row falls back to 19% for id 1 (the seeded Standard rate) and 0%
  // otherwise; a null id (no rate assigned) is treated as 0% (net == gross).
  const percentForRateId = (id: number | null): number => {
    if (id === null) return 0;
    const p = vatPercentById.get(id);
    if (p !== undefined) return p;
    return id === 1 ? 19 : 0;
  };
  // A procurement LINE inherits its VAT from the article (chain); default to
  // id 1 (19%) when the chain isn't resolved — matches the matrix.
  const linePercent = (line: ProcurementLineRow): number =>
    percentForRateId(chainByRoot.get(line.article_chain_root_id)?.vat_rate_id ?? 1);
  const netOf = (grossCents: number, percent: number): number =>
    grossToNet(grossCents, percent).netCents;
  // Render a stored GROSS amount in the active entry mode: net mode shows
  // its net at `percent`, brutto mode shows the gross verbatim.
  const displayInMode = (
    grossCents: number | null | undefined,
    percent: number,
  ): number | undefined => {
    if (grossCents === null || grossCents === undefined) return undefined;
    return netEntry ? netOf(grossCents, percent) : grossCents;
  };
  // Convert a value the operator TYPED (interpreted per the active mode) back
  // to the GROSS amount we persist: net mode runs net_to_gross, brutto mode
  // keeps it as-is.
  const grossFromTyped = (
    typed: number | null,
    percent: number,
  ): number | null => {
    if (typed === null) return null;
    return netEntry ? netToGross(typed, percent).grossCents : typed;
  };
  // Procurement expenses read "cost-positive" (invoice style): a cost is a
  // POSITIVE number, a credit / reimbursement is negative — matching the
  // article lines up top and the VAT matrix below. Storage is the DB bank
  // convention (cost negative), so every expense-value cell negates on the
  // way in and out. The flip is a plain sign change (displayInMode and
  // grossFromTyped are sign-symmetric). accounting.md §2.
  // Gross Pfand carried by a procurement line (units + crates).
  const linePfandGross = (line: ProcurementLineRow): number =>
    (line.pfand_value_cents_snapshot ?? 0) * line.qty +
    (line.crate_pfand_value_cents_snapshot ?? 0) * line.crate_count;
  // Active VAT options for the per-expense selector. Label mirrors the
  // catalogue dropdown ("xx% Name"). A non-active rate still in use on a row
  // is appended so the value resolves (handled per-cell via currentId).
  // value 0 = the "No VAT" sentinel (vat_rate ids always start at 1, so 0 is
  // safe) → clears the rate to null. Surfaced as an explicit dropdown entry
  // so the operator can go back to "no rate" after picking one, not only via
  // the easy-to-miss clear ✕.
  const vatSelectOptions = (currentId: number | null) => {
    const opts = vatRates
      .filter((v) => v.active || v.id === currentId)
      .map((v) => ({ value: v.id, label: `${Number(v.percent)}% ${v.name}` }));
    return [{ value: 0, label: "No VAT" }, ...opts];
  };

  // Read-only money cell: a stored GROSS amount shown either as net (the
  // converted figure) or brutto (verbatim). "—" when zero/empty.
  const moneyReadonly = (gross: number | null, percent: number, asNet: boolean) =>
    gross ? (
      <Text type="secondary">
        {formatCents(asNet ? netOf(gross, percent) : gross)}
      </Text>
    ) : (
      <Text type="secondary">—</Text>
    );

  // Editable money inputs for the expense table. The SAME input lives in the
  // net column (netto mode) or the brutto column (brutto mode) — it "jumps"
  // between the pair so the field always sits under the header matching what
  // the operator types. displayInMode/grossFromTyped already branch on the
  // active mode, and the focus buffer (editedValue) keeps the field from
  // resetting mid-typing. Each input is rendered in exactly ONE column per
  // mode, so the editKey never collides.
  const expenseValueInput = (exp: ExpenseRow) => {
    const percent = percentForRateId(exp.vat_rate_id);
    const editKey = `expense-${exp.id}-value`;
    const cents = exp.value_cents;
    return (
      <InputNumber
        value={editedValue(editKey, displayInMode(-cents, percent))}
        disabled={editingDisabled}
        addonAfter={netEntry ? "ct net" : "ct"}
        style={{ width: "100%" }}
        onChange={(v) => {
          const typed = typeof v === "number" ? v : null;
          setActiveEdit({ key: editKey, raw: typed });
          if (typed === null || !Number.isFinite(typed)) return;
          const next = -(grossFromTyped(typed, percent) ?? 0);
          if (next !== cents) {
            debouncedCommit(editKey, () =>
              patchExpenseField(exp, { value_cents: next }),
            );
          }
        }}
        onBlur={(e) => {
          const raw = e.target.value;
          const typed = raw === "" ? null : Number(raw);
          if (typed !== null && Number.isFinite(typed)) {
            const next = -(grossFromTyped(typed, percent) ?? 0);
            if (next !== cents) {
              optimisticExpense(exp.id, { value_cents: next });
              debouncedCommit(editKey, () =>
                patchExpenseField(exp, { value_cents: next }),
              );
            }
          }
          setActiveEdit(null);
        }}
      />
    );
  };
  // Pfand is always 19%, but it still flips to net entry when toggled (per
  // the operator's request on the expenses table).
  const expensePfandInput = (exp: ExpenseRow) => {
    const editKey = `expense-${exp.id}-pfand`;
    const cents = exp.pfand_value_cents;
    // Invoice-style like Value: display cost-positive (negate the stored
    // bank-signed cents) and negate back on the way in. Null stays null so the
    // "—" placeholder shows for an empty Pfand.
    return (
      <InputNumber
        value={editedValue(editKey, displayInMode(cents === null ? null : -cents, 19))}
        placeholder="—"
        disabled={editingDisabled}
        addonAfter={netEntry ? "ct net" : "ct"}
        style={{ width: "100%" }}
        onChange={(v) => {
          const typed = typeof v === "number" ? v : null;
          setActiveEdit({ key: editKey, raw: typed });
          const gross = grossFromTyped(typed, 19);
          const next = gross === null ? null : -gross;
          if (next !== cents) {
            debouncedCommit(editKey, () =>
              patchExpenseField(exp, { pfand_value_cents: next }),
            );
          }
        }}
        onBlur={(e) => {
          const raw = e.target.value;
          const typed = raw === "" ? null : Number(raw);
          const gross = grossFromTyped(typed, 19);
          const next = gross === null ? null : -gross;
          if (next !== cents && (next === null || Number.isFinite(next))) {
            optimisticExpense(exp.id, { pfand_value_cents: next });
            debouncedCommit(editKey, () =>
              patchExpenseField(exp, { pfand_value_cents: next }),
            );
          }
          setActiveEdit(null);
        }}
      />
    );
  };
  // Draft-row (add-expense) inputs — same jump behaviour, but local state
  // (no focus buffer / commit needed). Storage stays gross; display per mode.
  const draftValuePercent = () => percentForRateId(draftExpense.vat_rate_id);
  // Invoice-style entry: a plain positive is a cost, a leading minus is a
  // credit / reimbursement. No min — the minus is the credit affordance.
  const draftValueInput = () => (
    <InputNumber
      value={displayInMode(draftExpense.value_cents, draftValuePercent())}
      addonAfter={netEntry ? "ct net" : "ct"}
      placeholder={netEntry ? "Net cost (− credit)" : "Cost (− credit)"}
      style={{ width: "100%" }}
      onChange={(v) =>
        setDraftExpense((e) => ({
          ...e,
          value_cents: grossFromTyped(
            typeof v === "number" ? v : null,
            percentForRateId(e.vat_rate_id),
          ),
        }))
      }
    />
  );
  const draftPfandInput = () => (
    <InputNumber
      value={displayInMode(draftExpense.pfand_value_cents, 19)}
      addonAfter={netEntry ? "ct net" : "ct"}
      placeholder={netEntry ? "Net Pfand (− refund)" : "Pfand (− refund)"}
      style={{ width: "100%" }}
      onChange={(v) =>
        setDraftExpense((e) => ({
          ...e,
          pfand_value_cents: grossFromTyped(typeof v === "number" ? v : null, 19),
        }))
      }
    />
  );

  // Expense table columns. Same shape as the line items table for
  // visual consistency (Fix 2).
  const expenseColumns: ColumnsType<ExpenseRow> = [
    {
      title: "Category",
      dataIndex: "category_id",
      width: 160,
      render: (id: number, exp) => (
        <Select
          value={id}
          disabled={editingDisabled}
          style={{ width: "100%" }}
          showSearch
          optionFilterProp="label"
          options={expenseCategories
            .filter(
              (c) =>
                !(OPERATOR_HIDDEN_FOR_EXPENSES as readonly string[]).includes(
                  c.code,
                ),
            )
            .map((c) => ({ value: c.id, label: c.name }))}
          onChange={(v) => {
            if (typeof v === "number" && v !== id) {
              patchExpenseField(exp, { category_id: v });
            }
          }}
        />
      ),
    },
    {
      title: "Comment",
      dataIndex: "comment",
      render: (comment: string | null, exp) => (
        <Input
          value={comment ?? ""}
          placeholder="—"
          disabled={editingDisabled}
          maxLength={1024}
          onChange={(ev) => {
            const next = ev.target.value;
            const nextOrNull: string | null = next.length > 0 ? next : null;
            // Optimistic: splice the new comment into local state so the
            // Input stays controlled but reflects typing immediately.
            setExpenses((prev) =>
              prev.map((e) =>
                e.id === exp.id ? { ...e, comment: nextOrNull } : e,
              ),
            );
            // Debounced PATCH — collapses N keystrokes into one round-trip.
            debouncedCommit(`expense-${exp.id}-comment`, () =>
              patchExpenseField(exp, { comment: nextOrNull }),
            );
          }}
        />
      ),
    },
    {
      // VAT classification for the expense's Value portion. Pfand is always
      // 19% regardless (folded onto the id-1 row in the matrix + the Net
      // Pfand column). null → "no rate" (0% for net math). Editable inline.
      title: "VAT",
      dataIndex: "vat_rate_id",
      width: 150,
      render: (rateId: number | null, exp) => {
        if (exp.category_id === pfandCategoryId) {
          // Pure-Pfand rows have no Value to tax → VAT is N/A.
          return <Text type="secondary">—</Text>;
        }
        return (
          <Select
            // null shows as the explicit "No VAT" option (value 0).
            value={rateId ?? 0}
            disabled={editingDisabled}
            style={{ width: "100%" }}
            options={vatSelectOptions(rateId)}
            onChange={(v) =>
              patchExpenseField(exp, {
                vat_rate_id: typeof v === "number" && v > 0 ? v : null,
              })
            }
          />
        );
      },
    },
    {
      // Net Pfand (always 19%). In NETTO mode this column holds the editable
      // input (operator types net Pfand); in brutto mode it's the read-only
      // net display in front of the Pfand column.
      title: "Net Pfand 19%",
      dataIndex: "pfand_value_cents",
      width: 140,
      align: "right",
      render: (cents: number | null, exp) =>
        netEntry
          ? expensePfandInput(exp)
          : moneyReadonly(cents === null ? null : -cents, 19, true),
    },
    {
      // Pfand column — independent cash flow from Value (per accounting
      // model §16). Editable in BRUTTO mode; in netto mode it shows the
      // read-only brutto while the input lives in the Net Pfand column.
      title: "Pfand",
      dataIndex: "pfand_value_cents",
      width: 140,
      align: "right",
      render: (cents: number | null, exp) =>
        netEntry
          ? moneyReadonly(cents === null ? null : -cents, 19, false)
          : expensePfandInput(exp),
    },
    {
      // Net Value at the row's VAT rate. In NETTO mode this column holds the
      // editable input (operator types net value); in brutto mode it's the
      // read-only net display in front of the Value column.
      title: "Net Value",
      dataIndex: "value_cents",
      width: 140,
      align: "right",
      render: (cents: number, exp) => {
        if (exp.category_id === pfandCategoryId) {
          return <Text type="secondary">—</Text>;
        }
        return netEntry
          ? expenseValueInput(exp)
          : moneyReadonly(-cents, percentForRateId(exp.vat_rate_id), true);
      },
    },
    {
      // Inline-edit: typed value commits via debounce on change AND on
      // blur (same pattern as the line-items table above). Cost-positive
      // (invoice-style): the cell shows -value_cents so a cost reads
      // positive, and the typed value is negated back to the DB bank
      // convention on commit (a leading minus = a credit). accounting.md §2.
      // Editable in BRUTTO mode; in netto mode it shows the read-only brutto
      // while the input lives in the Net Value column.
      title: "Value",
      dataIndex: "value_cents",
      width: 140,
      align: "right",
      render: (cents: number, exp) => {
        if (exp.category_id === pfandCategoryId) {
          return <Text type="secondary">—</Text>;
        }
        return netEntry
          ? moneyReadonly(-cents, percentForRateId(exp.vat_rate_id), false)
          : expenseValueInput(exp);
      },
    },
    {
      title: "",
      width: 80,
      render: (_: unknown, exp) => (
        <Popconfirm
          title="Delete this expense?"
          okText="Delete"
          okButtonProps={{ danger: true }}
          onConfirm={() => void onDeleteExpense(exp)}
        >
          <Button type="text" danger>
            Remove
          </Button>
        </Popconfirm>
      ),
    },
  ];

  // VAT matrix (buchhalterisch; ONE combined table over Lines + Expenses).
  // Per VAT rate: goods net (lines at their article's rate, expenses at their
  // own) + Pfand net (ALWAYS 19%, summed over both tables, folded onto the
  // id-1 / 19% row). VAT tax is computed on the combined net so each row
  // reconciles: Value Netto + Pfand Netto + VAT tax = Value Brutto. Declared
  // BEFORE the early returns below to keep hook order stable.
  const vatMatrix = useMemo(() => {
    const rateById = new Map(vatRates.map((v) => [v.id, v]));
    const vatIdByChain = new Map(
      chains.map((c) => [c.chain_root_id, c.vat_rate_id]),
    );
    interface Group {
      key: number;
      percent: number;
      label: string;
      goodsNet: number;
      pfandNet: number;
    }
    const groups = new Map<number, Group>();
    const ensure = (id: number): Group => {
      let g = groups.get(id);
      if (!g) {
        const r = rateById.get(id);
        g = {
          key: id,
          percent: r ? r.percent : id === 1 ? 19 : 0,
          label: r ? r.name : id === 1 ? "Standard" : "No VAT",
          goodsNet: 0,
          pfandNet: 0,
        };
        groups.set(id, g);
      }
      return g;
    };
    const addGoods = (id: number, brutto: number) => {
      if (!brutto) return;
      const g = ensure(id);
      g.goodsNet += grossToNet(brutto, g.percent).netCents;
    };
    const addPfand = (brutto: number) => {
      if (!brutto) return;
      const g = ensure(1); // Pfand is always 19% → id 1
      g.pfandNet += grossToNet(brutto, g.percent).netCents;
    };
    for (const line of row?.lines ?? []) {
      const vatId = vatIdByChain.get(line.article_chain_root_id) ?? 1;
      addGoods(vatId, (line.unit_cost_cents ?? 0) * line.qty);
      addPfand(
        (line.pfand_value_cents_snapshot ?? 0) * line.qty +
          (line.crate_pfand_value_cents_snapshot ?? 0) * line.crate_count,
      );
    }
    for (const exp of expenses) {
      // Cost-positive (BOTH components): a cost (stored negative) ADDS to the
      // matrix like the line items above; a credit / Pfand refund (stored
      // positive) subtracts. accounting.md §2/§16.
      addGoods(exp.vat_rate_id ?? 0, -exp.value_cents);
      addPfand(-(exp.pfand_value_cents ?? 0));
    }
    return [...groups.values()]
      .filter((g) => g.goodsNet !== 0 || g.pfandNet !== 0)
      .sort((a, b) => b.percent - a.percent)
      .map((g) => {
        const totalNet = g.goodsNet + g.pfandNet;
        const { vatCents, grossCents } = netToGross(totalNet, g.percent);
        return { ...g, totalNet, vatTax: vatCents, brutto: grossCents };
      });
  }, [row?.lines, expenses, chains, vatRates]);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  if (!row && loading) {
    return (
      <Card>
        <Spin />
      </Card>
    );
  }
  if (!row) {
    return (
      <Card>
        <Alert
          type="error"
          message={error ?? "Procurement not found."}
          showIcon
        />
        <div style={{ marginTop: 16 }}>
          <Button onClick={() => navigate("/stock/procurement")}>← Back to list</Button>
        </div>
      </Card>
    );
  }

  const totals = (
    <Space size="large">
      <Text>
        Total cost: <Text strong>{formatCents(row.total_cost_cents)}</Text>
      </Text>
      <Text>
        Total Pfand: <Text strong>{formatCents(row.total_pfand_cents)}</Text>
      </Text>
    </Space>
  );

  const stateCtas = renderStateCtas(
    row.state,
    transition,
    lineEditDisabled,
  );

  // Fix 1 helper — pack size for a line's article (null when unknown,
  // so callers can decide whether to skip the qty ↔ crate sync).
  const packSizeFor = (line: ProcurementLineRow): number | null =>
    chainByRoot.get(line.article_chain_root_id)?.pack_size ?? null;

  // Fix 4 helper — total per line in cents.
  //
  // Pfand is intentionally NOT included here: it has its own dedicated
  // column, and supplier invoices list goods and Pfand on separate
  // lines, so the "Total" column should mirror that and surface just
  // the goods cost (unit_cost_cents × qty).
  const lineTotal = (line: ProcurementLineRow): number => {
    const cost = (line.unit_cost_cents ?? 0) * line.qty;
    return cost;
  };
  // Fix 1 — qty change on an existing line. If the line's crate_count is
  // still "in sync" (= floor(old_qty / pack)), keep it in sync by
  // patching crate_count alongside qty in one PATCH. Manual overrides
  // (tracked in `manualEdits`) skip the auto-derive.
  const patchLineQty = (line: ProcurementLineRow, nextQty: number) => {
    const patch: Partial<ProcurementLineInput> = { qty: nextQty };
    const pack = packSizeFor(line);
    if (pack !== null && pack > 0) {
      const inSync = line.crate_count === Math.floor(line.qty / pack);
      const cratesUntouched = isAuto(line.id, "crate_count");
      if (inSync || cratesUntouched) {
        patch.crate_count = Math.floor(nextQty / pack);
      }
    }
    markManual(line.id, "qty");
    void patchLine(line, patch);
  };

  // Fix 1 — crate_count change on an existing line. Crate edits are
  // authoritative: qty = crates * pack_size, always.
  const patchLineCrates = (line: ProcurementLineRow, nextCrates: number) => {
    const patch: Partial<ProcurementLineInput> = { crate_count: nextCrates };
    const pack = packSizeFor(line);
    if (pack !== null && pack > 0) {
      patch.qty = nextCrates * pack;
    }
    markManual(line.id, "crate_count");
    void patchLine(line, patch);
  };

  // -----------------------------------------------------------------
  // Fix 6 — draft-line derived values + change handlers.
  //
  // The draft mirrors the bidirectional qty ↔ crate logic from
  // patchLineQty / patchLineCrates, but operates on local state since
  // the draft has no server row yet (no PATCH until the operator hits
  // "+ Add"). Article change also prefills unit_cost from
  // lastBySupplierByArticle, mirroring Fix 1 for new lines.
  // -----------------------------------------------------------------
  const draftArticle = draftLine.article_chain_root_id
    ? chainByRoot.get(draftLine.article_chain_root_id) ?? null
    : null;
  // VAT percent for the draft line's article (default id 1 / 19%) — used to
  // convert net unit-cost entry to the stored gross.
  const draftPercent = percentForRateId(draftArticle?.vat_rate_id ?? 1);
  const draftPack = draftArticle?.pack_size ?? null;
  const draftPfandUnit =
    draftArticle?.pfand_value_id != null
      ? pfandValueById.get(draftArticle.pfand_value_id)?.cents ?? 0
      : 0;
  const draftPfandCrate =
    draftArticle?.crate_pfand_value_id != null
      ? pfandValueById.get(draftArticle.crate_pfand_value_id)?.cents ?? 0
      : 0;
  const draftTotalCents =
    (draftLine.unit_cost_cents ?? 0) * (draftLine.qty ?? 0) +
    draftPfandUnit * (draftLine.qty ?? 0) +
    draftPfandCrate * (draftLine.crate_count ?? 0);

  const onDraftArticleChange = (articleId: number | null) => {
    setDraftLine((d) => {
      const next: DraftLine = {
        ...d,
        article_chain_root_id: articleId,
        unit_cost_cents: null,
        crate_count: null,
      };
      if (articleId !== null) {
        const prefill = lastBySupplierByArticle.get(articleId);
        if (prefill !== undefined) next.unit_cost_cents = prefill;
      }
      return next;
    });
  };

  const onDraftQtyChange = (nextQty: number | null) => {
    setDraftLine((d) => {
      const next: DraftLine = { ...d, qty: nextQty };
      const pack = d.article_chain_root_id
        ? chainByRoot.get(d.article_chain_root_id)?.pack_size ?? null
        : null;
      if (pack !== null && pack > 0 && nextQty !== null) {
        next.crate_count = Math.floor(nextQty / pack);
      }
      return next;
    });
  };

  const onDraftCratesChange = (nextCrates: number | null) => {
    setDraftLine((d) => {
      const next: DraftLine = { ...d, crate_count: nextCrates };
      const pack = d.article_chain_root_id
        ? chainByRoot.get(d.article_chain_root_id)?.pack_size ?? null
        : null;
      if (pack !== null && pack > 0 && nextCrates !== null) {
        next.qty = nextCrates * pack;
      }
      return next;
    });
  };

  const lineColumns: ColumnsType<ProcurementLineRow> = [
    {
      // Fix 5 — line-id reference column. Helps the operator cross-
      // reference rows in the URL / backend logs.
      title: "#",
      dataIndex: "id",
      width: 60,
      render: (lineId: number) => <Text type="secondary">#{lineId}</Text>,
    },
    {
      title: "Article",
      dataIndex: "article_chain_root_id",
      render: (id: number, line) => (
        <Select
          value={id}
          options={articleOptions}
          showSearch
          optionFilterProp="label"
          style={{ minWidth: 220 }}
          disabled={lineEditDisabled}
          onChange={(next) =>
            void patchLine(line, { article_chain_root_id: next })
          }
        />
      ),
    },
    {
      title: "Qty",
      dataIndex: "qty",
      width: 110,
      render: (qty: number, line) => {
        const editKey = `line-${line.id}-qty`;
        return (
          <InputNumber
            min={0}
            // Focus buffer so an async commit/refetch can't reset the field
            // while the operator is typing a longer number.
            value={editedValue(editKey, qty)}
            disabled={lineEditDisabled}
            style={isAuto(line.id, "qty") ? TERTIARY_STYLE : undefined}
            // Fix 2 — arrow controls (▲/▼) fire onChange but not blur, so
            // commit here too (debounced) and keep onBlur as a fallback
            // for typed values when the user clicks away first.
            onChange={(v) => {
              const next = typeof v === "number" ? v : Number(v ?? 0);
              setActiveEdit({ key: editKey, raw: Number.isFinite(next) ? next : null });
              if (Number.isFinite(next) && next !== qty) {
                debouncedCommit(editKey, () => patchLineQty(line, next));
              }
            }}
            onBlur={(e) => {
              const next = Number(e.target.value);
              if (Number.isFinite(next) && next !== qty) {
                optimisticLine(line.id, { qty: next });
                debouncedCommit(editKey, () => patchLineQty(line, next));
              }
              setActiveEdit(null);
            }}
          />
        );
      },
    },
    {
      // In net mode the operator types the NET unit cost; we convert to
      // gross (per unit, at the article rate) on commit. Title flips so the
      // operator knows which side they're entering.
      title: netEntry ? "Unit cost (net)" : "Unit cost",
      dataIndex: "unit_cost_cents",
      width: 150,
      render: (cents: number | null, line) => {
        const percent = linePercent(line);
        // Fix 1 — with the auto-PATCH in place, `cents === null` should
        // happen only when no last-for-supplier suggestion exists. We
        // still surface a placeholder + tooltip on those rows so the
        // operator knows to type a value, and the copy now reflects the
        // "auto-filled, edit to override" behaviour. The ghost is a stored
        // gross figure → show it in the active mode too.
        const ghost =
          cents === null
            ? lastBySupplierByArticle.get(line.article_chain_root_id) ?? null
            : null;
        const ghostShown =
          ghost !== null ? displayInMode(ghost, percent) : undefined;
        const placeholder =
          ghostShown !== undefined ? `${ghostShown} (last)` : undefined;
        const editKey = `line-${line.id}-unit_cost`;
        return (
          <Tooltip
            title={
              ghost !== null
                ? `Auto-filled from last supplier order — edit to override.`
                : ""
            }
          >
            <InputNumber
              min={0}
              // While focused, render the operator's raw keystrokes from the
              // focus buffer so an async commit/refetch can't reset the field
              // mid-typing; otherwise show the stored gross in the active mode.
              value={editedValue(editKey, displayInMode(cents, percent))}
              addonAfter={netEntry ? "ct net" : "ct"}
              placeholder={placeholder}
              disabled={lineEditDisabled}
              style={
                isAuto(line.id, "unit_cost_cents") && cents !== null
                  ? TERTIARY_STYLE
                  : undefined
              }
              // Fix 2 — commit on arrow click (debounced) AND on blur as
              // a fallback for typed values. Arrow clicks are explicit
              // operator action, so markManual to drop the auto styling.
              // The typed value is interpreted per the active mode and
              // converted back to gross before persisting.
              onChange={(v) => {
                const typed =
                  v === null || v === undefined
                    ? null
                    : typeof v === "number"
                      ? v
                      : Number(v);
                setActiveEdit({ key: editKey, raw: typed });
                const gross = grossFromTyped(typed, percent);
                if (gross !== cents) {
                  debouncedCommit(editKey, () => {
                    markManual(line.id, "unit_cost_cents");
                    void patchLine(line, { unit_cost_cents: gross });
                  });
                }
              }}
              onBlur={(e) => {
                const raw = e.target.value;
                const typed = raw === "" ? null : Number(raw);
                const gross = grossFromTyped(typed, percent);
                if (gross !== cents) {
                  // Optimistic write first so dropping the buffer doesn't
                  // flash the pre-commit value.
                  optimisticLine(line.id, { unit_cost_cents: gross });
                  debouncedCommit(editKey, () => {
                    markManual(line.id, "unit_cost_cents");
                    void patchLine(line, { unit_cost_cents: gross });
                  });
                }
                setActiveEdit(null);
              }}
            />
          </Tooltip>
        );
      },
    },
    {
      title: "Crates",
      dataIndex: "crate_count",
      width: 110,
      render: (count: number, line) => {
        // Fix 4 — articles without a pack_size can't track crates;
        // collapse the cell to an em-dash with an explainer tooltip.
        const pack = packSizeFor(line);
        if (pack === null || pack <= 0) {
          return (
            <Tooltip title="Article has no pack size — set in catalog to enable crate tracking.">
              <Text type="secondary">—</Text>
            </Tooltip>
          );
        }
        const editKey = `line-${line.id}-crate`;
        return (
          <InputNumber
            min={0}
            // Focus buffer — see Qty/Unit cost above.
            value={editedValue(editKey, count)}
            disabled={lineEditDisabled}
            style={isAuto(line.id, "crate_count") ? TERTIARY_STYLE : undefined}
            // Fix 2 — arrow controls commit immediately (debounced).
            onChange={(v) => {
              const next = typeof v === "number" ? v : Number(v ?? 0);
              setActiveEdit({ key: editKey, raw: Number.isFinite(next) ? next : null });
              if (Number.isFinite(next) && next !== count) {
                debouncedCommit(editKey, () => patchLineCrates(line, next));
              }
            }}
            onBlur={(e) => {
              const next = Number(e.target.value);
              if (Number.isFinite(next) && next !== count) {
                optimisticLine(line.id, { crate_count: next });
                debouncedCommit(editKey, () => patchLineCrates(line, next));
              }
              setActiveEdit(null);
            }}
          />
        );
      },
    },
    {
      title: "Pfand (snap)",
      render: (_: unknown, line) => {
        const unit = line.pfand_value_cents_snapshot ?? 0;
        const crate = line.crate_pfand_value_cents_snapshot ?? 0;
        return (
          <Text type="secondary">
            {formatCents(unit)} / {formatCents(crate)} crate
          </Text>
        );
      },
    },
    {
      // Net of the line's Pfand (always 19%). Read-only display, shown in
      // front of the Pfand column per the net-entry spec.
      title: "Net Pfand 19%",
      width: 120,
      align: "right",
      render: (_: unknown, line) => {
        const pfand = linePfandGross(line);
        return pfand ? (
          <Text type="secondary">{formatCents(netOf(pfand, 19))}</Text>
        ) : (
          <Text type="secondary">—</Text>
        );
      },
    },
    {
      // Computed Pfand for the line — per-unit × qty + per-crate × crate_count.
      // Surfaced as a dedicated column so the operator can read goods cost
      // (Total below) vs Pfand cycle separately rather than mentally
      // subtracting one from the other.
      title: "Pfand",
      width: 110,
      align: "right",
      render: (_: unknown, line) => {
        const pfandUnit = (line.pfand_value_cents_snapshot ?? 0) * line.qty;
        const cratePfand =
          (line.crate_pfand_value_cents_snapshot ?? 0) * line.crate_count;
        const total = pfandUnit + cratePfand;
        return <Text>{formatCents(total)}</Text>;
      },
    },
    {
      // Net of the goods total at the line's article VAT rate. Read-only
      // display, shown in front of the Total column per the net-entry spec.
      title: "Net total",
      width: 120,
      align: "right",
      render: (_: unknown, line) => {
        const total = lineTotal(line);
        return total ? (
          <Text type="secondary">
            {formatCents(netOf(total, linePercent(line)))}
          </Text>
        ) : (
          <Text type="secondary">—</Text>
        );
      },
    },
    {
      title: "Total",
      width: 110,
      align: "right",
      render: (_: unknown, line) => (
        <Text strong>{formatCents(lineTotal(line))}</Text>
      ),
    },
    {
      title: "",
      width: 80,
      render: (_: unknown, line) => (
        <Popconfirm
          title="Delete this line?"
          okText="Delete"
          okButtonProps={{ danger: true }}
          onConfirm={() => void deleteLine(line)}
          disabled={lineEditDisabled}
        >
          <Button type="text" danger disabled={lineEditDisabled}>
            Remove
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <Card>
      <Space
        style={{ width: "100%", justifyContent: "space-between", marginBottom: 16 }}
      >
        <Space>
          <Button onClick={() => navigate("/stock/procurement")}>← Back</Button>
          <Title level={3} style={{ margin: 0 }}>
            Procurement #{row.id}
          </Title>
          <StatePill state={row.state} />
        </Space>
      </Space>

      {stocktakeBlock && !stocktakeBlockCollapsed && (
        <Alert
          type="warning"
          showIcon
          banner
          message={
            <>
              Editing blocked — stocktake #{stocktakeBlock.stocktake_id} is in
              progress (started {formatCompactDateTime(stocktakeBlock.started_at)}).
            </>
          }
          description={
            <Space wrap>
              <Button
                type="primary"
                onClick={() => navigate(`/stock/stocktakes`)}
              >
                Go to stocktake
              </Button>
              <Button onClick={() => setStocktakeBlockCollapsed(true)}>
                Wait for it to finish
              </Button>
              <Text type="secondary">Polling for finalize every 15s…</Text>
            </Space>
          }
          style={{ marginBottom: 16 }}
        />
      )}
      {stocktakeBlock && stocktakeBlockCollapsed && (
        // Collapsed pill — the 15s polling is still running; clicking
        // re-expands so the operator can re-read the deep link.
        <div
          style={{
            position: "fixed",
            right: 16,
            bottom: 16,
            zIndex: 1000,
          }}
        >
          <Tooltip title={`Waiting on stocktake #${stocktakeBlock.stocktake_id} — click to expand`}>
            <Tag
              color="warning"
              style={{ cursor: "pointer", padding: "4px 10px", fontSize: 13 }}
              onClick={() => setStocktakeBlockCollapsed(false)}
            >
              Waiting…
            </Tag>
          </Tooltip>
        </div>
      )}

      {staleBanner && (
        <Alert
          type="info"
          showIcon
          message="Someone else edited this — reload?"
          action={
            <Button size="small" type="primary" onClick={() => void refresh()}>
              Reload
            </Button>
          }
          style={{ marginBottom: 16 }}
        />
      )}

      {error && (
        <Alert
          type="error"
          closable
          onClose={() => setError(null)}
          message={error}
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Header section — Descriptions auto-stacks on narrow viewports. */}
      <Descriptions
        bordered
        size="small"
        column={isMobile ? 1 : 2}
        style={{ marginBottom: 16 }}
      >
        <Descriptions.Item label="Supplier">
          <SupplierPicker
            value={row.supplier ?? ""}
            suggestions={supplierSuggestions}
            onQueryChange={setSupplierQuery}
            onCommit={onSupplierCommit}
            disabled={editingDisabled}
          />
        </Descriptions.Item>
        <Descriptions.Item label="Invoice ref">
          <Input
            // `key` ties this uncontrolled input to the current row
            // snapshot. When a stale-banner Reload (or any other
            // refresh) replaces `row`, the new key remounts the
            // Input so its visible value re-syncs to the latest
            // server `invoice_ref` — defaultValue alone is
            // mount-only, so without this the input would silently
            // keep showing the pre-reload draft.
            key={`invoice-${row.updated_at}`}
            defaultValue={row.invoice_ref ?? ""}
            placeholder="—"
            disabled={editingDisabled}
            onBlur={(e) => {
              const next = e.target.value.trim();
              if (next !== (row.invoice_ref ?? "")) {
                void patchHeader({ invoice_ref: next || null });
              }
            }}
          />
        </Descriptions.Item>
        <Descriptions.Item label="Delivery date">
          <DatePicker
            value={row.delivery_date ? dayjs(row.delivery_date) : null}
            disabled={editingDisabled}
            onChange={(d) =>
              void patchHeader({
                delivery_date: d ? d.format("YYYY-MM-DD") : null,
              })
            }
            format="DD.MM.YYYY"
          />
        </Descriptions.Item>
        <Descriptions.Item label="Received at">
          {/* Read-only until the procurement enters ``received`` state
              (backend manages the field transition). Once received, a
              [Change] button opens the backdate modal — server-enforced
              floor + global stocktake lock guard the input. See
              openspec/changes/add-procurement-backdate. */}
          <Space size={8} wrap>
            <Text>
              {row.received_at ? formatCompactDateTime(row.received_at) : "—"}
            </Text>
            {row.state === "received" && (
              <Button
                size="small"
                onClick={() => setBackdateOpen(true)}
                disabled={editingDisabled}
              >
                Change
              </Button>
            )}
          </Space>
        </Descriptions.Item>
        <Descriptions.Item label="Totals" span={isMobile ? 1 : 2}>
          {totals}
        </Descriptions.Item>
        <Descriptions.Item label="Comment" span={isMobile ? 1 : 2}>
          <Input.TextArea
            key={`comment-${row.updated_at}`}
            defaultValue={row.comment ?? ""}
            rows={2}
            disabled={editingDisabled}
            onBlur={(e) => {
              const next = e.target.value.trim();
              if (next !== (row.comment ?? "")) {
                void patchHeader({ comment: next || null });
              }
            }}
          />
        </Descriptions.Item>
      </Descriptions>

      {/* Lines */}
      <Space
        style={{ width: "100%", justifyContent: "space-between", marginBottom: 8 }}
      >
        <Space size="large" wrap>
          <Title level={4} style={{ margin: 0 }}>
            Lines ({row.lines.length})
          </Title>
          {/* Net/brutto entry switch (Part 2). Governs the editable goods
              fields in BOTH tables (Lines unit cost + Expenses value). */}
          <Space size={8}>
            <Segmented<"brutto" | "netto">
              value={netEntry ? "netto" : "brutto"}
              onChange={(v) => setNetEntry(v === "netto")}
              options={[
                { value: "brutto", label: "Brutto entry" },
                { value: "netto", label: "Netto entry" },
              ]}
            />
            {netEntry && (
              <Tooltip title="You type net unit cost / net value; the price is converted to brutto on save (commercial rounding — expect ≤1 cent). Pfand stays brutto. Stored values are always gross.">
                <Text type="secondary" style={{ fontSize: 12 }}>
                  typing net → saved as brutto ⓘ
                </Text>
              </Tooltip>
            )}
          </Space>
        </Space>
        {row.state === "draft" && row.supplier && (
          <Button onClick={() => void copyFromLastOrder()} loading={copyBusy}>
            Copy lines from last order
          </Button>
        )}
      </Space>

      {isMobile ? (
        <>
          <MobileLinesList
            lines={row.lines}
            articleNameById={articleNameById}
            articleOptions={articleOptions}
            onPatch={patchLine}
            onDelete={deleteLine}
            onPatchQty={patchLineQty}
            onPatchCrates={patchLineCrates}
            lineTotal={lineTotal}
            lineNet={(line) => ({
              goods: netOf(lineTotal(line), linePercent(line)),
              pfand: netOf(linePfandGross(line), 19),
            })}
            netEntry={netEntry}
            linePercent={linePercent}
            isAuto={isAuto}
            markManual={markManual}
            lastBySupplierByArticle={lastBySupplierByArticle}
            packSizeForMobile={packSizeFor}
            disabled={lineEditDisabled}
          />
          {/* Fix 6 — mobile add-line card. Mirrors the desktop summary
              row as a stacked card at the bottom of the list so the
              add affordance stays visible without re-introducing the
              old AntD Form. */}
          <Card size="small" style={{ marginTop: 8 }}>
            <Space direction="vertical" style={{ width: "100%" }} size={6}>
              <Text strong>Add line</Text>
              <Select
                value={draftLine.article_chain_root_id ?? undefined}
                options={articleOptions}
                showSearch
                optionFilterProp="label"
                placeholder="Pick article…"
                style={{ width: "100%" }}
                disabled={lineEditDisabled}
                onChange={(v) =>
                  onDraftArticleChange(typeof v === "number" ? v : null)
                }
              />
              <Space wrap>
                <InputNumber
                  min={0}
                  value={draftLine.qty ?? undefined}
                  addonBefore="Qty"
                  disabled={lineEditDisabled}
                  onChange={(v) =>
                    onDraftQtyChange(typeof v === "number" ? v : null)
                  }
                />
                <InputNumber
                  min={0}
                  value={displayInMode(draftLine.unit_cost_cents, draftPercent)}
                  addonAfter={netEntry ? "ct net" : "ct"}
                  placeholder={netEntry ? "Net unit cost" : "Unit cost"}
                  disabled={lineEditDisabled}
                  onChange={(v) =>
                    setDraftLine((d) => ({
                      ...d,
                      unit_cost_cents: grossFromTyped(
                        typeof v === "number" ? v : null,
                        draftPercent,
                      ),
                    }))
                  }
                />
              </Space>
              {draftPack ? (
                <InputNumber
                  min={0}
                  value={draftLine.crate_count ?? undefined}
                  addonBefore="Crates"
                  disabled={lineEditDisabled}
                  onChange={(v) =>
                    onDraftCratesChange(typeof v === "number" ? v : null)
                  }
                />
              ) : draftLine.article_chain_root_id ? (
                <Tooltip title="Article has no pack size — set in catalog to enable crate tracking.">
                  <Text type="secondary">Crates: —</Text>
                </Tooltip>
              ) : null}
              <Space style={{ justifyContent: "space-between", width: "100%" }}>
                <Text type="secondary">
                  Total: <Text strong>{formatCents(draftTotalCents)}</Text>
                </Text>
                <Button
                  type="primary"
                  disabled={
                    lineEditDisabled ||
                    draftLine.article_chain_root_id === null ||
                    draftLine.qty === null ||
                    draftLine.qty <= 0
                  }
                  onClick={() => void onAddDraftLine()}
                >
                  + Add
                </Button>
              </Space>
            </Space>
          </Card>
        </>
      ) : (
        <Table<ProcurementLineRow>
          columns={lineColumns}
          dataSource={row.lines}
          rowKey="id"
          pagination={false}
          size="small"
          // Fix 6 — render the draft row as a Table.Summary footer.
          // Replaces the old <Form layout="inline"> below the table.
          // Two summary rows: a totals row at the top (sum of existing
          // line items), then the draft (add-line) row below it.
          summary={() => (
            <>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={6}>
                  <Text strong>Totals</Text>
                </Table.Summary.Cell>
                {/* Net Pfand 19% Σ */}
                <Table.Summary.Cell index={6} align="right">
                  <Text type="secondary">
                    {formatCents(
                      row.lines.reduce((s, l) => {
                        const p = linePfandGross(l);
                        return s + (p ? netOf(p, 19) : 0);
                      }, 0),
                    )}
                  </Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={7} align="right">
                  <Text strong>{formatCents(row.total_pfand_cents)}</Text>
                </Table.Summary.Cell>
                {/* Net total Σ (goods, at each line's article VAT rate) */}
                <Table.Summary.Cell index={8} align="right">
                  <Text type="secondary">
                    {formatCents(
                      row.lines.reduce(
                        (s, l) => s + netOf(lineTotal(l), linePercent(l)),
                        0,
                      ),
                    )}
                  </Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={9} align="right">
                  <Text strong>{formatCents(row.total_cost_cents)}</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={10} />
              </Table.Summary.Row>
            <Table.Summary.Row>
              <Table.Summary.Cell index={0}>
                <Text type="secondary">+</Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1}>
                <Select
                  value={draftLine.article_chain_root_id ?? undefined}
                  options={articleOptions}
                  showSearch
                  optionFilterProp="label"
                  placeholder="Pick article…"
                  style={{ minWidth: 220 }}
                  disabled={lineEditDisabled}
                  onChange={(v) =>
                    onDraftArticleChange(typeof v === "number" ? v : null)
                  }
                />
              </Table.Summary.Cell>
              <Table.Summary.Cell index={2}>
                <InputNumber
                  min={0}
                  value={draftLine.qty ?? undefined}
                  placeholder="Qty"
                  disabled={lineEditDisabled}
                  onChange={(v) =>
                    onDraftQtyChange(typeof v === "number" ? v : null)
                  }
                />
              </Table.Summary.Cell>
              <Table.Summary.Cell index={3}>
                <InputNumber
                  min={0}
                  value={displayInMode(draftLine.unit_cost_cents, draftPercent)}
                  addonAfter={netEntry ? "ct net" : "ct"}
                  placeholder={netEntry ? "Net unit cost" : "Unit cost"}
                  disabled={lineEditDisabled}
                  onChange={(v) =>
                    setDraftLine((d) => ({
                      ...d,
                      unit_cost_cents: grossFromTyped(
                        typeof v === "number" ? v : null,
                        draftPercent,
                      ),
                    }))
                  }
                />
              </Table.Summary.Cell>
              <Table.Summary.Cell index={4}>
                {draftPack ? (
                  <InputNumber
                    min={0}
                    value={draftLine.crate_count ?? undefined}
                    placeholder="Crates"
                    disabled={lineEditDisabled}
                    onChange={(v) =>
                      onDraftCratesChange(typeof v === "number" ? v : null)
                    }
                  />
                ) : (
                  <Text type="secondary">—</Text>
                )}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={5}>
                {draftArticle && (
                  <Text type="secondary">
                    {formatCents(draftPfandUnit)} /{" "}
                    {formatCents(draftPfandCrate)} crate
                  </Text>
                )}
              </Table.Summary.Cell>
              {/* Net Pfand 19% (read-only preview of the draft Pfand) */}
              <Table.Summary.Cell index={6} align="right">
                {draftArticle &&
                  draftPfandUnit * (draftLine.qty ?? 0) +
                    draftPfandCrate * (draftLine.crate_count ?? 0) >
                    0 && (
                    <Text type="secondary">
                      {formatCents(
                        netOf(
                          draftPfandUnit * (draftLine.qty ?? 0) +
                            draftPfandCrate * (draftLine.crate_count ?? 0),
                          19,
                        ),
                      )}
                    </Text>
                  )}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={7} align="right">
                {draftArticle && (
                  <Text>
                    {formatCents(
                      draftPfandUnit * (draftLine.qty ?? 0) +
                        draftPfandCrate * (draftLine.crate_count ?? 0),
                    )}
                  </Text>
                )}
              </Table.Summary.Cell>
              {/* Net total (read-only preview at the draft article VAT rate) */}
              <Table.Summary.Cell index={8} align="right">
                {(draftLine.unit_cost_cents ?? 0) * (draftLine.qty ?? 0) > 0 && (
                  <Text type="secondary">
                    {formatCents(
                      netOf(
                        (draftLine.unit_cost_cents ?? 0) * (draftLine.qty ?? 0),
                        percentForRateId(draftArticle?.vat_rate_id ?? 1),
                      ),
                    )}
                  </Text>
                )}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={9} align="right">
                <Text strong>
                  {formatCents(
                    (draftLine.unit_cost_cents ?? 0) * (draftLine.qty ?? 0),
                  )}
                </Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={10}>
                <Button
                  type="primary"
                  disabled={
                    lineEditDisabled ||
                    draftLine.article_chain_root_id === null ||
                    draftLine.qty === null ||
                    draftLine.qty <= 0
                  }
                  onClick={() => void onAddDraftLine()}
                >
                  + Add
                </Button>
              </Table.Summary.Cell>
            </Table.Summary.Row>
            </>
          )}
        />
      )}

      <Divider style={{ marginTop: 32 }} />

      {/* Expenses & Pfand returns. Same table-shape as the line items
          above for visual consistency. Fix 7 — the "+ Add expense"
          Modal was replaced with an inline draft row rendered as a
          Table.Summary footer; the Pfand calculator (previously an
          expandable block inside the Modal) is now a Card below the
          table that surfaces when category === Pfand. */}
      <Space
        style={{ width: "100%", justifyContent: "space-between", marginBottom: 8 }}
      >
        <Title level={4} style={{ margin: 0 }}>
          Expenses & Pfand returns
        </Title>
      </Space>
      <Table<ExpenseRow>
        columns={expenseColumns}
        dataSource={expenses}
        rowKey="id"
        pagination={false}
        size="small"
        locale={{ emptyText: "No expenses linked to this procurement." }}
        summary={() => (
          <>
            {/* Totals row: sum of existing expenses. Drawer-flow rows
                (Cash drawer category) are included naively here — the
                "is this a P&L event" filtering lives elsewhere; this
                preview is the column-sum operators expect to see under
                their inputs. The Value/Net Value AND Pfand/Net Pfand Σ are
                all cost-positive (negated) to match the per-row cells above. */}
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={3}>
                <Text strong>Totals</Text>
              </Table.Summary.Cell>
              {/* Net Pfand 19% Σ */}
              <Table.Summary.Cell index={3} align="right">
                <Text type="secondary">
                  {formatCents(
                    expenses.reduce(
                      (s, e) =>
                        s +
                        (e.pfand_value_cents
                          ? netOf(-e.pfand_value_cents, 19)
                          : 0),
                      0,
                    ),
                  )}
                </Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={4} align="right">
                <Text strong>
                  {formatCents(
                    expenses.reduce(
                      (s, e) => s - (e.pfand_value_cents ?? 0),
                      0,
                    ),
                  )}
                </Text>
              </Table.Summary.Cell>
              {/* Net Value Σ */}
              <Table.Summary.Cell index={5} align="right">
                <Text type="secondary">
                  {formatCents(
                    expenses.reduce(
                      (s, e) =>
                        s + netOf(-e.value_cents, percentForRateId(e.vat_rate_id)),
                      0,
                    ),
                  )}
                </Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={6} align="right">
                <Text strong>
                  {formatCents(
                    expenses.reduce((s, e) => s - e.value_cents, 0),
                  )}
                </Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={7} />
            </Table.Summary.Row>
          <Table.Summary.Row>
            {/* Category */}
            <Table.Summary.Cell index={0}>
              <Space direction="vertical" size={2} style={{ width: "100%" }}>
                <Select
                  value={draftExpense.category_id ?? undefined}
                  status={draftExpense.category_id === null ? "warning" : undefined}
                  // Drawer-flow categories are managed via the Cash drawer
                  // page; stock_loss rows auto-cascade from stocktakes.
                  // Suppress both here to prevent operators creating
                  // either as procurement extras by hand.
                  options={expenseCategories
                    .filter(
                      (c) =>
                        !(
                          OPERATOR_HIDDEN_FOR_EXPENSES as readonly string[]
                        ).includes(c.code),
                    )
                    .map((c) => ({ value: c.id, label: c.name }))}
                  showSearch
                  optionFilterProp="label"
                  placeholder="Category"
                  style={{ minWidth: 140 }}
                  onChange={(v) =>
                    setDraftExpense((e) => ({
                      ...e,
                      category_id: typeof v === "number" ? v : null,
                    }))
                  }
                />
                {draftExpense.category_id === null && (
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                    Pick a category to enable Add.
                  </Typography.Text>
                )}
              </Space>
            </Table.Summary.Cell>
            {/* Comment */}
            <Table.Summary.Cell index={1}>
              <Input
                value={draftExpense.comment}
                placeholder="Optional comment"
                maxLength={1024}
                onChange={(ev) =>
                  setDraftExpense((e) => ({
                    ...e,
                    comment: ev.target.value,
                  }))
                }
              />
            </Table.Summary.Cell>
            {/* VAT: rate for the Value portion. N/A on pure-Pfand rows. */}
            <Table.Summary.Cell index={2}>
              {draftExpense.category_id !== null &&
              draftExpense.category_id === pfandCategoryId ? (
                <Text type="secondary">—</Text>
              ) : (
                <Select
                  value={draftExpense.vat_rate_id ?? 0}
                  style={{ width: "100%" }}
                  options={vatSelectOptions(draftExpense.vat_rate_id)}
                  onChange={(v) =>
                    setDraftExpense((e) => ({
                      ...e,
                      vat_rate_id: typeof v === "number" && v > 0 ? v : null,
                    }))
                  }
                />
              )}
            </Table.Summary.Cell>
            {/* Net Pfand 19% — editable in netto mode, else read-only net. */}
            <Table.Summary.Cell index={3} align="right">
              {netEntry
                ? draftPfandInput()
                : moneyReadonly(draftExpense.pfand_value_cents, 19, true)}
            </Table.Summary.Cell>
            {/* Pfand (independent signed cents, accounting.md §16) — editable
                in brutto mode, else read-only brutto. */}
            <Table.Summary.Cell index={4} align="right">
              {netEntry
                ? moneyReadonly(draftExpense.pfand_value_cents, 19, false)
                : draftPfandInput()}
            </Table.Summary.Cell>
            {/* Net Value — editable in netto mode, else read-only net. */}
            <Table.Summary.Cell index={5} align="right">
              {draftExpense.category_id !== null &&
              draftExpense.category_id === pfandCategoryId ? (
                <Text type="secondary">—</Text>
              ) : netEntry ? (
                draftValueInput()
              ) : (
                moneyReadonly(
                  draftExpense.value_cents,
                  percentForRateId(draftExpense.vat_rate_id),
                  true,
                )
              )}
            </Table.Summary.Cell>
            {/* Value — editable in brutto mode, else read-only brutto.
                Hidden (—) when category=Pfand: pure-Pfand rows have
                value_cents=0 by definition. */}
            <Table.Summary.Cell index={6} align="right">
              {draftExpense.category_id !== null &&
              draftExpense.category_id === pfandCategoryId ? (
                <Text type="secondary">—</Text>
              ) : netEntry ? (
                moneyReadonly(
                  draftExpense.value_cents,
                  percentForRateId(draftExpense.vat_rate_id),
                  false,
                )
              ) : (
                draftValueInput()
              )}
            </Table.Summary.Cell>
            {/* Action */}
            <Table.Summary.Cell index={7}>
              <Space>
                {draftExpense.category_id !== null &&
                  draftExpense.category_id === pfandCategoryId && (
                    <Button
                      type="link"
                      size="small"
                      onClick={() => setCalcSectionOpen((v) => !v)}
                    >
                      {calcSectionOpen ? "Hide calc" : "Calc"}
                    </Button>
                  )}
                <Button
                  type="primary"
                  size="small"
                  disabled={
                    draftExpense.category_id === null ||
                    !Number.isFinite(draftExpense.category_id) ||
                    draftExpense.category_id <= 0 ||
                    // At least one of Value / Pfand must be non-zero
                    // — both null/zero produces an all-zero row the
                    // schema rejects.
                    !(
                      (draftExpense.value_cents !== null &&
                        Number.isFinite(draftExpense.value_cents) &&
                        draftExpense.value_cents !== 0) ||
                      (draftExpense.pfand_value_cents !== null &&
                        Number.isFinite(draftExpense.pfand_value_cents) &&
                        draftExpense.pfand_value_cents !== 0)
                    )
                  }
                  onClick={() => void onAddDraftExpense()}
                >
                  + Add
                </Button>
              </Space>
            </Table.Summary.Cell>
          </Table.Summary.Row>
          </>
        )}
      />

      {/* Fix 7 — Pfand calculator card. Rendered below the table when
          the draft expense's category is Pfand (mirroring the old
          Modal-embedded calculator). Output non-destructively fills
          draft magnitude + comment via applyCalculator. */}
      {draftExpense.category_id !== null &&
        draftExpense.category_id === pfandCategoryId &&
        calcSectionOpen && (
          <Card size="small" style={{ marginTop: 8 }} title="Pfand calculator">
            <Space
              direction="vertical"
              style={{ width: "100%" }}
              size={8}
            >
              <Radio.Group
                value={calcMode}
                onChange={(e) =>
                  setCalcMode(e.target.value as "article" | "pfand_type")
                }
              >
                <Radio.Button value="article">From article</Radio.Button>
                <Radio.Button value="pfand_type">From Pfand type</Radio.Button>
              </Radio.Group>
              {calcMode === "article" ? (
                <Space wrap>
                  <Select
                    value={calcArticleId ?? undefined}
                    onChange={(v) => setCalcArticleId(v as number)}
                    options={pfandArticleOptions}
                    showSearch
                    optionFilterProp="label"
                    placeholder="Article…"
                    style={{ minWidth: 280 }}
                  />
                  <InputNumber
                    min={0}
                    value={calcCratesReturned}
                    onChange={(v) => setCalcCratesReturned(Number(v ?? 0))}
                    addonBefore="Crates"
                  />
                </Space>
              ) : (
                <Space wrap>
                  <Select
                    value={calcPfandValueId ?? undefined}
                    onChange={(v) => setCalcPfandValueId(v as number)}
                    options={pfandValues.map((p) => ({
                      value: p.id,
                      label: `${p.label} (${formatCents(p.cents)})`,
                    }))}
                    placeholder="Pfand type…"
                    style={{ minWidth: 280 }}
                  />
                  <InputNumber
                    min={0}
                    value={calcQty}
                    onChange={(v) => setCalcQty(Number(v ?? 0))}
                    addonBefore="Qty"
                  />
                </Space>
              )}
              <Space>
                <Text type="secondary">Computed:</Text>
                <Text strong>
                  {calculatorPreview
                    ? formatCents(calculatorPreview.cents)
                    : "—"}
                </Text>
                <Button
                  size="small"
                  disabled={!calculatorPreview}
                  onClick={applyCalculator}
                >
                  Use this value
                </Button>
              </Space>
              {calculatorPreview && (
                <Text type="secondary">
                  Will set comment to: {calculatorPreview.comment}
                </Text>
              )}
            </Space>
          </Card>
        )}

      {/* Bill total — mirrors the backend's bill_total_cents semantic.
          We subtract drawer-flow extras (Cash drawer / Cash drawer
          opening / Cash variance) from the procurement totals so the
          numbers shown match what an operator sees on the supplier's
          invoice: goods + Pfand for the line items + non-drawer extras
          attached to this procurement. The semantic ledger view
          (drawer flow vs P&L) lives elsewhere. Drawer-flow set is
          pinned by ``code`` (post-migration-0024) so a rename of any
          drawer category doesn't silently change the bill math. */}
      {(() => {
        const linkedExtrasValue = expenses
          .filter((e) => {
            const cat = expenseCategoryById.get(e.category_id);
            return (
              !cat ||
              !(DRAWER_FLOW_CODES as readonly string[]).includes(cat.code)
            );
          })
          .reduce((s, e) => s + e.value_cents, 0);
        const linkedExtrasPfand = expenses
          .filter((e) => {
            const cat = expenseCategoryById.get(e.category_id);
            return (
              !cat ||
              !(DRAWER_FLOW_CODES as readonly string[]).includes(cat.code)
            );
          })
          .reduce((s, e) => s + (e.pfand_value_cents ?? 0), 0);
        const billPfandTotal = row.total_pfand_cents - linkedExtrasPfand;
        const billCostTotal = row.total_cost_cents - linkedExtrasValue;
        const billGrandTotal = billCostTotal + billPfandTotal;
        return (
          <Card size="small" style={{ marginTop: 16 }}>
            {/* Tier 1: subtotals across both tables (brutto). */}
            <Row gutter={16} align="middle" style={{ marginBottom: 12 }}>
              <Col flex="auto">
                <Text type="secondary">Subtotals (both tables)</Text>
              </Col>
              <Col style={{ width: 140, textAlign: "right" }}>
                <Text strong>{formatCents(billPfandTotal)}</Text>
              </Col>
              <Col style={{ width: 140, textAlign: "right" }}>
                <Text strong>{formatCents(billCostTotal)}</Text>
              </Col>
              {/* Spacer matching the tables' rightmost remove/actions column
                  so the Pfand + value totals line up under their columns. */}
              <Col style={{ width: 80 }} />
            </Row>
            {/* VAT summary across BOTH tables — buchhalterisch (net). Each
                row reconciles: Value Netto + Pfand Netto + VAT tax = Brutto;
                Pfand (always 19%) sits only on the id-1 row. */}
            <Text type="secondary">VAT summary — both tables (net)</Text>
            <table
              style={{
                borderCollapse: "collapse",
                fontSize: 13,
                marginTop: 6,
                marginBottom: 12,
              }}
            >
              <thead>
                <tr style={{ color: "var(--ant-color-text-secondary)" }}>
                  <th style={{ textAlign: "left", padding: "2px 16px 6px 0" }}>VAT</th>
                  <th style={{ textAlign: "right", padding: "2px 16px 6px" }}>Value netto</th>
                  <th style={{ textAlign: "right", padding: "2px 16px 6px" }}>Pfand netto</th>
                  <th style={{ textAlign: "right", padding: "2px 16px 6px" }}>Total netto</th>
                  <th style={{ textAlign: "right", padding: "2px 16px 6px" }}>VAT tax</th>
                  <th style={{ textAlign: "right", padding: "2px 0 6px 16px" }}>Brutto</th>
                </tr>
              </thead>
              <tbody>
                {vatMatrix.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ padding: "2px 0" }}>
                      <Text type="secondary">No priced rows yet.</Text>
                    </td>
                  </tr>
                ) : (
                  vatMatrix.map((g) => (
                    <tr key={g.key}>
                      <td style={{ textAlign: "left", padding: "2px 16px 2px 0" }}>
                        <Text>
                          {Number(g.percent)}% {g.label}
                        </Text>
                      </td>
                      <td style={{ textAlign: "right", padding: "2px 16px" }}>
                        {formatCents(g.goodsNet)}
                      </td>
                      <td
                        style={{
                          textAlign: "right",
                          padding: "2px 16px",
                          color: "var(--ant-color-text-secondary)",
                        }}
                      >
                        {g.pfandNet ? formatCents(g.pfandNet) : "—"}
                      </td>
                      <td style={{ textAlign: "right", padding: "2px 16px" }}>
                        {formatCents(g.totalNet)}
                      </td>
                      <td style={{ textAlign: "right", padding: "2px 16px" }}>
                        {formatCents(g.vatTax)}
                      </td>
                      <td style={{ textAlign: "right", padding: "2px 0 2px 16px" }}>
                        <Text strong>{formatCents(g.brutto)}</Text>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            {/* Tier 2: bill grand total, bottom-right, slightly larger */}
            <Row justify="end">
              <Col>
                <Space size={12} align="baseline">
                  <Text strong style={{ fontSize: 18 }}>
                    Bill total
                  </Text>
                  <Text strong style={{ fontSize: 20 }}>
                    {formatCents(billGrandTotal)}
                  </Text>
                </Space>
              </Col>
            </Row>
          </Card>
        );
      })()}

      {/* State-transition footer. The "Delete draft" affordance lives
          alongside the state CTAs (only rendered when the procurement
          is still in draft and editing isn't blocked by an in-progress
          stocktake) — keeps destructive actions next to their cohort
          rather than scattering them across the header. */}
      <Space style={{ marginTop: 24, flexWrap: "wrap" }}>
        {stateCtas}
        {row.state === "draft" && (
          <Popconfirm
            title="Delete this draft?"
            description="This procurement and all its lines will be permanently removed. Cannot be undone."
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => void onDeleteDraft()}
            disabled={lineEditDisabled}
          >
            <Button danger disabled={lineEditDisabled}>
              Delete draft
            </Button>
          </Popconfirm>
        )}
      </Space>

      {row.state === "received" && (
        <BackdateReceivedAtModal
          open={backdateOpen}
          row={row}
          paidInCash={paidInCash}
          onClose={() => setBackdateOpen(false)}
          onSuccess={(next) => setRow(next)}
          onApiError={(err) => {
            handleEditError(err);
          }}
        />
      )}
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Mobile lines list — stacks each line as a tappable card with inline
// number / select editors. Mirrors the desktop columns but reads less
// densely.
// ---------------------------------------------------------------------------

interface MobileLinesListProps {
  lines: ProcurementLineRow[];
  articleNameById: Map<number, string>;
  articleOptions: { value: number; label: string }[];
  onPatch: (
    line: ProcurementLineRow,
    next: Partial<ProcurementLineInput>,
  ) => void | Promise<void>;
  onDelete: (line: ProcurementLineRow) => void | Promise<void>;
  /** Fix 1 — bidirectional qty ↔ crate handlers (parent owns the
   * pack_size lookup + manual-edit tracking). */
  onPatchQty: (line: ProcurementLineRow, nextQty: number) => void;
  onPatchCrates: (line: ProcurementLineRow, nextCrates: number) => void;
  /** Fix 4 — total per line in cents. */
  lineTotal: (line: ProcurementLineRow) => number;
  /** Buchhalterisch net of goods (at the article VAT rate) + Pfand (19%)
   * for the line — read-only display alongside the brutto figures. */
  lineNet: (line: ProcurementLineRow) => { goods: number; pfand: number };
  /** Net/brutto entry mode — in net mode the unit-cost input accepts a net
   * per-unit figure (converted to gross on save). */
  netEntry: boolean;
  /** VAT percent for a line's article — drives the net↔gross conversion. */
  linePercent: (line: ProcurementLineRow) => number;
  /** Fix 3 — auto-vs-typed lookup. */
  isAuto: (lineKey: string | number, field: string) => boolean;
  /** Fix 3 — marker for explicit operator edits. */
  markManual: (lineKey: string | number, field: string) => void;
  /** Fix 2 — chain_root_id → last-for-supplier unit_cost (ghost hint). */
  lastBySupplierByArticle: Map<number, number>;
  /** Fix 4 — pack size lookup so we can hide crates for articles
   * without one. Returns null when the article isn't crate-tracked. */
  packSizeForMobile: (line: ProcurementLineRow) => number | null;
  disabled: boolean;
}

const MobileLinesList = ({
  lines,
  articleNameById,
  articleOptions,
  onPatch,
  onDelete,
  onPatchQty,
  onPatchCrates,
  lineTotal,
  lineNet,
  netEntry,
  linePercent,
  isAuto,
  markManual,
  lastBySupplierByArticle,
  packSizeForMobile,
  disabled,
}: MobileLinesListProps) => {
  if (lines.length === 0) {
    return <Text type="secondary">No lines yet — add one below.</Text>;
  }
  return (
    <Space direction="vertical" style={{ width: "100%" }}>
      {lines.map((line) => {
        const ghostCost =
          line.unit_cost_cents === null
            ? lastBySupplierByArticle.get(line.article_chain_root_id) ?? null
            : null;
        return (
          <Card key={line.id} size="small">
            <Space direction="vertical" style={{ width: "100%" }} size={6}>
              <Select
                value={line.article_chain_root_id}
                options={articleOptions}
                showSearch
                optionFilterProp="label"
                disabled={disabled}
                style={{ width: "100%" }}
                onChange={(next) =>
                  void onPatch(line, { article_chain_root_id: next })
                }
              />
              <Space>
                <InputNumber
                  min={0}
                  value={line.qty}
                  disabled={disabled}
                  addonBefore="Qty"
                  style={isAuto(line.id, "qty") ? TERTIARY_STYLE : undefined}
                  onBlur={(e) => {
                    const next = Number(e.target.value);
                    if (Number.isFinite(next) && next !== line.qty) {
                      onPatchQty(line, next);
                    }
                  }}
                />
                <InputNumber
                  min={0}
                  value={
                    line.unit_cost_cents === null
                      ? undefined
                      : netEntry
                        ? grossToNet(line.unit_cost_cents, linePercent(line))
                            .netCents
                        : line.unit_cost_cents
                  }
                  disabled={disabled}
                  addonBefore={netEntry ? "ct net" : "ct"}
                  placeholder={
                    ghostCost !== null
                      ? `${
                          netEntry
                            ? grossToNet(ghostCost, linePercent(line)).netCents
                            : ghostCost
                        } (last)`
                      : undefined
                  }
                  style={
                    isAuto(line.id, "unit_cost_cents") &&
                    line.unit_cost_cents !== null
                      ? TERTIARY_STYLE
                      : undefined
                  }
                  onBlur={(e) => {
                    const raw = e.target.value;
                    const typed = raw === "" ? null : Number(raw);
                    const gross =
                      typed === null
                        ? null
                        : netEntry
                          ? netToGross(typed, linePercent(line)).grossCents
                          : typed;
                    if (gross !== line.unit_cost_cents) {
                      markManual(line.id, "unit_cost_cents");
                      void onPatch(line, { unit_cost_cents: gross });
                    }
                  }}
                />
              </Space>
              <Space>
                {/* Fix 4 — same hide-crates rule on mobile when the
                    article has no pack size. */}
                {packSizeForMobile(line) ? (
                  <InputNumber
                    min={0}
                    value={line.crate_count}
                    disabled={disabled}
                    addonBefore="Crates"
                    style={
                      isAuto(line.id, "crate_count") ? TERTIARY_STYLE : undefined
                    }
                    onBlur={(e) => {
                      const next = Number(e.target.value);
                      if (Number.isFinite(next) && next !== line.crate_count) {
                        onPatchCrates(line, next);
                      }
                    }}
                  />
                ) : (
                  <Tooltip title="Article has no pack size — set in catalog to enable crate tracking.">
                    <Text type="secondary">Crates: —</Text>
                  </Tooltip>
                )}
                <Text type="secondary">
                  Pfand snap: {formatCents(line.pfand_value_cents_snapshot)} /{" "}
                  {formatCents(line.crate_pfand_value_cents_snapshot)} crate
                </Text>
              </Space>
              <Space style={{ justifyContent: "space-between", width: "100%" }}>
                <Text type="secondary">
                  {/* Never fall back to a bare "#N" — the Article column
                      surfaces the operator-recognisable name (see Fix 1). */}
                  {articleNameById.get(line.article_chain_root_id) ??
                    `Article #${line.article_chain_root_id}`}
                </Text>
                <Popconfirm
                  title="Delete this line?"
                  okText="Delete"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => void onDelete(line)}
                  disabled={disabled}
                >
                  <Button type="text" danger disabled={disabled}>
                    Remove
                  </Button>
                </Popconfirm>
              </Space>
              {/* Fix 4 — total per line on its own row at the card's
                  bottom. Right-aligned to mirror the desktop column.
                  Net (goods at the article VAT rate, Pfand at 19%) shown
                  underneath as a read-only secondary line. */}
              <div style={{ textAlign: "right" }}>
                <Text strong>Total: {formatCents(lineTotal(line))}</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Net {formatCents(lineNet(line).goods)}
                  {lineNet(line).pfand > 0 && (
                    <> · Net Pfand {formatCents(lineNet(line).pfand)}</>
                  )}
                </Text>
              </div>
            </Space>
          </Card>
        );
      })}
    </Space>
  );
};

// ---------------------------------------------------------------------------
// "Mark received" CTA — pops a Popconfirm with an inline checkbox so the
// operator can flag the delivery as paid-in-cash. The flag flows through
// to the state-transition PATCH; the backend writes a linked
// ``Cash drawer`` expense when set. Idempotent server-side, so an
// accidental double-click is harmless (the second call no-ops).
// ---------------------------------------------------------------------------

interface MarkReceivedButtonProps {
  disabled: boolean;
  transition: (
    next: ProcurementState,
    options?: { paid_in_cash?: boolean },
  ) => void | Promise<void>;
}

const MarkReceivedButton = ({
  disabled,
  transition,
}: MarkReceivedButtonProps) => {
  const [open, setOpen] = useState(false);
  const [paidInCash, setPaidInCash] = useState(false);
  return (
    <Popconfirm
      title="Mark received"
      description={
        <div style={{ maxWidth: 280 }}>
          <Typography.Paragraph style={{ marginBottom: 8 }}>
            Move this procurement into the received state. The buy-price
            timeline gets the matching entries.
          </Typography.Paragraph>
          <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <input
              type="checkbox"
              checked={paidInCash}
              onChange={(e) => setPaidInCash(e.target.checked)}
            />
            Paid in cash from drawer
          </label>
        </div>
      }
      okText="Mark received"
      open={open}
      onOpenChange={setOpen}
      onConfirm={() => {
        void transition("received", { paid_in_cash: paidInCash });
        setPaidInCash(false);
      }}
      onCancel={() => setPaidInCash(false)}
      disabled={disabled}
    >
      <Button type="primary" disabled={disabled}>
        Mark received
      </Button>
    </Popconfirm>
  );
};


// ---------------------------------------------------------------------------
// State CTAs — keyed off the current state. Each button's onClick is the
// target state; the backend enforces the transitions, but we hide the
// ones we know are illegal so the operator doesn't waste a click.
// ---------------------------------------------------------------------------

function renderStateCtas(
  current: ProcurementState,
  transition: (
    next: ProcurementState,
    options?: { paid_in_cash?: boolean },
  ) => void | Promise<void>,
  disabled: boolean,
): React.ReactNode {
  if (current === "draft") {
    return (
      <>
        <MarkReceivedButton disabled={disabled} transition={transition} />
        <Popconfirm
          title="Cancel this procurement?"
          okText="Cancel it"
          onConfirm={() => void transition("cancelled")}
          disabled={disabled}
        >
          <Button danger disabled={disabled}>
            Cancel
          </Button>
        </Popconfirm>
      </>
    );
  }
  if (current === "received") {
    return (
      <>
        <Popconfirm
          title="Move back to draft? Buy-price rows will be reversed."
          okText="Move to draft"
          onConfirm={() => void transition("draft")}
          disabled={disabled}
        >
          <Button disabled={disabled}>Move back to draft</Button>
        </Popconfirm>
        <Popconfirm
          title="Cancel this procurement? Buy-price rows will be reversed."
          okText="Cancel it"
          okButtonProps={{ danger: true }}
          onConfirm={() => void transition("cancelled")}
          disabled={disabled}
        >
          <Button danger disabled={disabled}>
            Cancel
          </Button>
        </Popconfirm>
      </>
    );
  }
  // cancelled — §3.6 enumerates BOTH `cancelled → received` (Reopen) and
  // `cancelled → draft` (Move to draft). The latter is gated behind a
  // Popconfirm because reverting to draft is destructive in the sense
  // that the cancelled-at-some-time semantics is lost; the backend
  // handles the buy-price-timeline reversal so the operator just needs
  // to confirm they actually meant the editable-draft destination.
  //
  // The "Cancel" CTA in §6.4 is documented as "always" available; in
  // the cancelled branch it's a semantic no-op, so we render it as a
  // disabled-but-visible "Already cancelled" indicator — spec-fidelity
  // without offering an action that would only confuse the operator.
  return (
    <>
      <Button
        type="primary"
        onClick={() => void transition("received")}
        disabled={disabled}
      >
        Reopen
      </Button>
      <Popconfirm
        title="Move this cancelled procurement back to draft?"
        description="Reverts the cancellation so lines become editable again. Buy-price rows stay closed (draft contributes nothing to the timeline)."
        okText="Move to draft"
        onConfirm={() => void transition("draft")}
        disabled={disabled}
      >
        <Button disabled={disabled}>Move to draft</Button>
      </Popconfirm>
      <Tooltip title="This procurement is already cancelled.">
        <Button danger disabled>
          Already cancelled
        </Button>
      </Tooltip>
    </>
  );
}

export default ProcurementDetailPage;
